/**
 * Elite Batch 7 Part 3 — Workflow Templates 598–622
 * Finance & Accounting workflows: ARR/MRR dashboards, AP/AR automation,
 * billing bots, budget management, commission processing, expense workflows,
 * cash flow projections, crypto alerts, error budgets, and more.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function zendeskNode(
  id: string,
  label: string,
  y: number,
  subject: string,
  body: string,
  priority: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
        headers: [
          { key: "Authorization", value: "Bearer {zendesk_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "ticket",
            value: `{"subject":"${subject}","comment":{"body":"${body}"},"priority":"${priority}"}`,
          },
        ],
        variables: vars,
      },
    },
    x,
  );
}
function salesforceNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{sf_instance}.salesforce.com/services/data/v59.0/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {salesforce_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}

// =============================================================================
// ELITE BATCH 7 PART 3 — TEMPLATES 598–622
// =============================================================================

export const ELITE_BATCH7_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 598. ARR MRR DASHBOARD UPDATER (8 nodes)
  // ===================================================================

  "ARR MRR Dashboard Updater": {
    summary:
      "Keeps ARR/MRR/NRR metrics current with AI trend analysis and executive alerts on threshold breaches. A daily schedule triggers Stripe subscription data pull, runCode-based MRR/ARR/NRR calculation, AI trend explanation, threshold breach condition routing, executive Slack alert on breaches, Google Sheets dashboard update, and metric audit trail logging.",
    nodes: [
      trigger("Schedule: Daily ARR/MRR dashboard refresh", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Runs daily at 7 AM to pull current subscription metrics, compute ARR/MRR/NRR, and update the executive dashboard",
        setup:
          "Configure Stripe API credentials and Google Sheets workbook ID. Set threshold values in the runCode node for breach alerting.",
        inputFields: [],
      }),

      // 2. Stripe subscription pull
      stripeNode(
        "2",
        "Pull active subscriptions from Stripe",
        280,
        "GET",
        "subscriptions?status=active&limit=100&expand[]=data.items",
        [],
        [
          { name: "data", customName: "subscriptions_data", type: "string" },
          { name: "has_more", customName: "has_more", type: "boolean" },
          {
            name: "data.length",
            customName: "active_sub_count",
            type: "number",
          },
        ],
      ),

      // 3. MRR/ARR/NRR calculation (runCode)
      n("3", "runCode", "Compute MRR, ARR & NRR metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse subscription data and compute key revenue metrics
const subs = JSON.parse(vars.subscriptions_data || "[]");
let totalMRR = 0;
let expansionMRR = 0;
let contractionMRR = 0;
let churnedMRR = 0;
const activeCount = parseInt(vars.active_sub_count || "0", 10);

subs.forEach(sub => {
  const monthlyAmount = (sub.items?.data || []).reduce((sum, item) => {
    const unitAmount = item.price?.unit_amount || 0;
    const qty = item.quantity || 1;
    const interval = item.price?.recurring?.interval || "month";
    const monthly = interval === "year" ? (unitAmount * qty) / 12 : unitAmount * qty;
    return sum + monthly;
  }, 0) / 100;
  totalMRR += monthlyAmount;
});

const arr = totalMRR * 12;
const previousMRR = totalMRR * 0.97; // baseline approximation
const nrr = previousMRR > 0 ? ((totalMRR - churnedMRR) / previousMRR * 100) : 100;

// Threshold checks
const mrrGrowthPct = previousMRR > 0 ? ((totalMRR - previousMRR) / previousMRR * 100) : 0;
const breachDetected = mrrGrowthPct < -5 || nrr < 95 || totalMRR < 10000;

return {
  total_mrr: totalMRR.toFixed(2),
  total_arr: arr.toFixed(2),
  net_revenue_retention: nrr.toFixed(1),
  active_subscriptions: activeCount,
  mrr_growth_pct: mrrGrowthPct.toFixed(2),
  breach_detected: breachDetected ? "yes" : "no",
  metric_summary: "MRR: $" + totalMRR.toFixed(0) + " | ARR: $" + arr.toFixed(0) + " | NRR: " + nrr.toFixed(1) + "% | Subs: " + activeCount
};`,
          outputVariables: [
            { name: "total_mrr", type: "string" },
            { name: "total_arr", type: "string" },
            { name: "net_revenue_retention", type: "string" },
            { name: "active_subscriptions", type: "number" },
            { name: "mrr_growth_pct", type: "string" },
            { name: "breach_detected", type: "string" },
            { name: "metric_summary", type: "string" },
          ],
        },
      }),

      // 4. AI trend explanation
      aiNode(
        "4",
        "AI explain revenue trends",
        760,
        "You are a SaaS revenue analyst. Given current MRR, ARR, NRR, and growth metrics, provide an executive-friendly trend explanation with actionable insights. Respond JSON: {trend_explanation: string, health_grade: string, top_risk: string, recommendation: string}",
        "MRR: ${total_mrr} | ARR: ${total_arr} | NRR: {net_revenue_retention}% | Growth: {mrr_growth_pct}% | Active subs: {active_subscriptions} | Breach: {breach_detected}",
        [
          {
            field: "trend_explanation",
            as: "trend_explanation",
            type: "string",
          },
          { field: "health_grade", as: "health_grade", type: "string" },
          { field: "top_risk", as: "top_risk", type: "string" },
          { field: "recommendation", as: "recommendation", type: "string" },
        ],
      ),

      // 5. Threshold breach condition
      n("5", "condition", "Revenue threshold breached?", 1000, {
        conditionData: [
          {
            variable: "breach_detected",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Executive Slack alert (True path)
      slackNode(
        "6",
        "Executive Slack: Revenue breach alert",
        1240,
        "🚨 *Revenue Threshold Breach*\\nHealth: {health_grade} | Growth: {mrr_growth_pct}%\\nMRR: ${total_mrr} | ARR: ${total_arr} | NRR: {net_revenue_retention}%\\nRisk: {top_risk}\\nAction: {recommendation}\\nTrend: {trend_explanation}",
      ),

      // 7. Dashboard update (Sheets)
      sheetsNode(
        "7",
        "Update ARR/MRR dashboard (Sheets)",
        1480,
        "RevenueDashboard",
        '[["{total_mrr}","{total_arr}","{net_revenue_retention}","{active_subscriptions}","{mrr_growth_pct}","{health_grade}","{trend_explanation}","{breach_detected}"]]',
      ),

      // 8. Metric audit trail
      sheetsNode(
        "8",
        "Log metric audit trail",
        1720,
        "MetricAudit",
        '[["{total_mrr}","{total_arr}","{net_revenue_retention}","{mrr_growth_pct}","{health_grade}","{top_risk}","{recommendation}","{breach_detected}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Breach → executive alert
      e("5", "7", "output_2"), // No breach → dashboard update directly
      e("6", "7"), // After alert → dashboard update
      e("7", "8"), // Dashboard → audit trail
    ],
  },

  // ===================================================================
  // 599. ACCOUNTS PAYABLE THREE-WAY MATCH (9 nodes)
  // ===================================================================

  "Accounts Payable Three-Way Match": {
    summary:
      "Automates AP three-way matching between invoices, purchase orders, and goods received notes. A vendor invoice webhook triggers AI extraction, PO and GRN fetch via API, runCode three-way comparison with tolerance checks, match/mismatch condition routing, auto-approval or dispute handling, payment scheduling, and AP dashboard logging.",
    nodes: [
      trigger("Webhook: Vendor invoice received", "webhook_app", {
        source: "AP System / Email Parser",
        event: "invoice.received",
        howItStarts:
          "A vendor invoice is received via email or AP system, triggering the three-way match process",
        setup:
          "Connect your AP system or email parser to POST invoice data (PDF or structured) to the workflow URL. Ensure PO and GRN systems are accessible via API.",
        inputFields: [],
      }),

      // 2. AI invoice extraction
      aiNode(
        "2",
        "AI extract invoice details",
        280,
        "You are an AP specialist. Extract all financial details from this vendor invoice regardless of format. Find vendor name, invoice number, PO reference number, line items with descriptions and amounts, total amount, currency, payment terms, and due date. Respond JSON: {vendor_name: string, invoice_number: string, po_number: string, line_items_summary: string, line_item_count: number, invoice_total: number, currency: string, payment_terms: string, due_date: string, vendor_email: string}",
        "{webhook_payload}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "po_number", as: "po_number", type: "string" },
          {
            field: "line_items_summary",
            as: "line_items_summary",
            type: "string",
          },
          { field: "line_item_count", as: "line_item_count", type: "number" },
          { field: "invoice_total", as: "invoice_total", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "vendor_email", as: "vendor_email", type: "string" },
        ],
      ),

      // 3. PO fetch
      n("3", "hitApi", "Fetch purchase order details", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{erp_api}/api/v1/purchase-orders/{po_number}?include=lines,approvals",
          headers: [
            { key: "Authorization", value: "Bearer {erp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.total_amount",
              customName: "po_total",
              type: "number",
            },
            {
              name: "data.line_count",
              customName: "po_line_count",
              type: "number",
            },
            { name: "data.status", customName: "po_status", type: "string" },
            {
              name: "data.lines_summary",
              customName: "po_lines_summary",
              type: "string",
            },
            {
              name: "data.approved_by",
              customName: "po_approver",
              type: "string",
            },
          ],
        },
      }),

      // 4. GRN fetch
      n("4", "hitApi", "Fetch goods received note", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://{erp_api}/api/v1/goods-receipts?po_number={po_number}&include=quantities,dates",
          headers: [
            { key: "Authorization", value: "Bearer {erp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.received_total",
              customName: "grn_total",
              type: "number",
            },
            {
              name: "data.received_line_count",
              customName: "grn_line_count",
              type: "number",
            },
            {
              name: "data.receipt_date",
              customName: "grn_date",
              type: "string",
            },
            {
              name: "data.grn_number",
              customName: "grn_number",
              type: "string",
            },
            {
              name: "data.discrepancy_notes",
              customName: "grn_notes",
              type: "string",
            },
          ],
        },
      }),

      // 5. Three-way comparison (runCode)
      n("5", "runCode", "Three-way match comparison", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Three-way match: Invoice vs PO vs GRN
const invoiceTotal = parseFloat(vars.invoice_total || "0");
const poTotal = parseFloat(vars.po_total || "0");
const grnTotal = parseFloat(vars.grn_total || "0");
const invoiceLines = parseInt(vars.line_item_count || "0", 10);
const poLines = parseInt(vars.po_line_count || "0", 10);
const grnLines = parseInt(vars.grn_line_count || "0", 10);
const tolerance = 0.02; // 2% tolerance

const invPoDiff = Math.abs(invoiceTotal - poTotal);
const invGrnDiff = Math.abs(invoiceTotal - grnTotal);
const invPoMatch = poTotal > 0 ? (invPoDiff / poTotal <= tolerance) : false;
const invGrnMatch = grnTotal > 0 ? (invGrnDiff / grnTotal <= tolerance) : false;
const linesMatch = invoiceLines === poLines && poLines === grnLines;
const fullMatch = invPoMatch && invGrnMatch && linesMatch;
const matchScore = (invPoMatch ? 33 : 0) + (invGrnMatch ? 33 : 0) + (linesMatch ? 34 : 0);

let mismatchReasons = [];
if (!invPoMatch) mismatchReasons.push("Invoice-PO amount diff: $" + invPoDiff.toFixed(2));
if (!invGrnMatch) mismatchReasons.push("Invoice-GRN amount diff: $" + invGrnDiff.toFixed(2));
if (!linesMatch) mismatchReasons.push("Line count mismatch: Inv=" + invoiceLines + " PO=" + poLines + " GRN=" + grnLines);

return {
  match_result: fullMatch ? "matched" : "mismatched",
  match_score: matchScore,
  inv_po_match: invPoMatch ? "yes" : "no",
  inv_grn_match: invGrnMatch ? "yes" : "no",
  lines_match: linesMatch ? "yes" : "no",
  mismatch_reasons: mismatchReasons.join("; ") || "None",
  match_summary: "Invoice #" + vars.invoice_number + ": $" + invoiceTotal.toFixed(2) + " vs PO: $" + poTotal.toFixed(2) + " vs GRN: $" + grnTotal.toFixed(2) + " | Score: " + matchScore + "%"
};`,
          outputVariables: [
            { name: "match_result", type: "string" },
            { name: "match_score", type: "number" },
            { name: "inv_po_match", type: "string" },
            { name: "inv_grn_match", type: "string" },
            { name: "lines_match", type: "string" },
            { name: "mismatch_reasons", type: "string" },
            { name: "match_summary", type: "string" },
          ],
        },
      }),

      // 6. Match condition
      n("6", "condition", "Three-way match passed?", 1240, {
        conditionData: [
          {
            variable: "match_result",
            value: "matched",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Auto-approval path (True) / dispute path (False)
      emailNode(
        "7",
        "Auto-approve & notify vendor",
        1480,
        "{vendor_email}",
        "Invoice #{invoice_number} approved — payment scheduled",
        "Dear {vendor_name},\\n\\nYour invoice #{invoice_number} for {currency} {invoice_total} has passed three-way matching (PO #{po_number}, GRN #{grn_number}) and is approved for payment.\\n\\nPayment terms: {payment_terms}\\nDue date: {due_date}\\n\\nMatch score: {match_score}%\\n\\nThank you for your business.",
      ),

      // 7b. Dispute / review path (False)
      emailNode(
        "7b",
        "Flag mismatch for review",
        1480,
        "{ap_team_email}",
        "⚠️ Three-way match FAILED: Invoice #{invoice_number} from {vendor_name}",
        "Three-way match failed for Invoice #{invoice_number}:\\n\\nVendor: {vendor_name}\\nInvoice total: {currency} {invoice_total}\\nPO total: {currency} {po_total}\\nGRN total: {currency} {grn_total}\\n\\nMatch score: {match_score}%\\nMismatch reasons: {mismatch_reasons}\\n\\nPlease review and resolve before payment can proceed.",
        400,
      ),

      // 8. Payment scheduling
      n("8", "hitApi", "Schedule payment in AP system", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://{erp_api}/api/v1/payments/schedule",
          headers: [
            { key: "Authorization", value: "Bearer {erp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "invoice_number", value: "{invoice_number}" },
            { key: "vendor_name", value: "{vendor_name}" },
            { key: "amount", value: "{invoice_total}" },
            { key: "currency", value: "{currency}" },
            { key: "due_date", value: "{due_date}" },
            { key: "po_number", value: "{po_number}" },
          ],
          variables: [
            {
              name: "data.payment_id",
              customName: "payment_id",
              type: "string",
            },
            {
              name: "data.scheduled_date",
              customName: "scheduled_payment_date",
              type: "string",
            },
          ],
        },
      }),

      // 9. AP dashboard (Sheets)
      sheetsNode(
        "9",
        "Log to AP dashboard (Sheets)",
        1960,
        "APDashboard",
        '[["{invoice_number}","{vendor_name}","{invoice_total}","{po_number}","{grn_number}","{match_result}","{match_score}","{mismatch_reasons}","{payment_id}","{due_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // Matched → auto-approve
      e("6", "7b", "output_2"), // Mismatched → dispute review
      e("7", "8"), // Approved → schedule payment
      e("8", "9"), // Payment → dashboard log
    ],
  },

  // ===================================================================
  // 600. ACCOUNTS RECEIVABLE AGING (8 nodes)
  // ===================================================================

  "Accounts Receivable Aging": {
    summary:
      "Weekly AR aging analysis with risk scoring and automated collection triggers. A weekly schedule pulls open invoices from Stripe, computes aging buckets via runCode, AI scores risk by customer, routes 90+ day invoices through a condition, emails finance a weekly aging report, triggers collection reminders for overdue accounts, and logs an aging snapshot to Sheets.",
    nodes: [
      trigger("Schedule: Weekly AR aging analysis", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Runs every Monday at 8 AM to analyze open invoices, compute aging buckets, and trigger collection workflows",
        setup:
          "Configure Stripe API credentials. Set aging bucket thresholds in the runCode node. Connect Google Sheets for snapshot logging.",
        inputFields: [],
      }),

      // 2. Open invoice pull (Stripe)
      stripeNode(
        "2",
        "Pull open invoices from Stripe",
        280,
        "GET",
        "invoices?status=open&limit=100&expand[]=data.customer",
        [],
        [
          { name: "data", customName: "open_invoices_data", type: "string" },
          {
            name: "data.length",
            customName: "total_open_invoices",
            type: "number",
          },
        ],
      ),

      // 3. Aging bucket computation (runCode)
      n("3", "runCode", "Compute aging buckets", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute aging buckets from open invoices
const invoices = JSON.parse(vars.open_invoices_data || "[]");
const now = Date.now() / 1000;
const buckets = { current: 0, days_30: 0, days_60: 0, days_90: 0, days_90_plus: 0 };
const bucketCounts = { current: 0, days_30: 0, days_60: 0, days_90: 0, days_90_plus: 0 };
let totalOutstanding = 0;
let criticalCustomers = [];

invoices.forEach(inv => {
  const dueDate = inv.due_date || inv.created;
  const daysOverdue = Math.floor((now - dueDate) / 86400);
  const amount = (inv.amount_due || 0) / 100;
  totalOutstanding += amount;

  if (daysOverdue <= 0) { buckets.current += amount; bucketCounts.current++; }
  else if (daysOverdue <= 30) { buckets.days_30 += amount; bucketCounts.days_30++; }
  else if (daysOverdue <= 60) { buckets.days_60 += amount; bucketCounts.days_60++; }
  else if (daysOverdue <= 90) { buckets.days_90 += amount; bucketCounts.days_90++; }
  else {
    buckets.days_90_plus += amount;
    bucketCounts.days_90_plus++;
    criticalCustomers.push((inv.customer?.name || inv.customer?.email || "Unknown") + ": $" + amount.toFixed(2));
  }
});

const has_90_plus = buckets.days_90_plus > 0 ? "yes" : "no";
return {
  total_outstanding: totalOutstanding.toFixed(2),
  bucket_current: buckets.current.toFixed(2),
  bucket_30: buckets.days_30.toFixed(2),
  bucket_60: buckets.days_60.toFixed(2),
  bucket_90: buckets.days_90.toFixed(2),
  bucket_90_plus: buckets.days_90_plus.toFixed(2),
  count_90_plus: bucketCounts.days_90_plus,
  has_90_plus: has_90_plus,
  critical_customers: criticalCustomers.slice(0, 10).join("; ") || "None",
  aging_summary: "Outstanding: $" + totalOutstanding.toFixed(0) + " | Current: $" + buckets.current.toFixed(0) + " | 30d: $" + buckets.days_30.toFixed(0) + " | 60d: $" + buckets.days_60.toFixed(0) + " | 90d: $" + buckets.days_90.toFixed(0) + " | 90+: $" + buckets.days_90_plus.toFixed(0)
};`,
          outputVariables: [
            { name: "total_outstanding", type: "string" },
            { name: "bucket_current", type: "string" },
            { name: "bucket_30", type: "string" },
            { name: "bucket_60", type: "string" },
            { name: "bucket_90", type: "string" },
            { name: "bucket_90_plus", type: "string" },
            { name: "count_90_plus", type: "number" },
            { name: "has_90_plus", type: "string" },
            { name: "critical_customers", type: "string" },
            { name: "aging_summary", type: "string" },
          ],
        },
      }),

      // 4. Risk scoring (AI)
      aiNode(
        "4",
        "AI risk-score aging portfolio",
        760,
        "You are an AR risk analyst. Given aging bucket data, assess overall portfolio health, identify highest-risk segments, and recommend collection strategies. Respond JSON: {risk_grade: string, risk_narrative: string, collection_priority: string, recommended_actions: string, dso_estimate: number}",
        "Total outstanding: ${total_outstanding} | Current: ${bucket_current} | 30d: ${bucket_30} | 60d: ${bucket_60} | 90d: ${bucket_90} | 90+: ${bucket_90_plus} ({count_90_plus} invoices) | Critical customers: {critical_customers}",
        [
          { field: "risk_grade", as: "risk_grade", type: "string" },
          { field: "risk_narrative", as: "risk_narrative", type: "string" },
          {
            field: "collection_priority",
            as: "collection_priority",
            type: "string",
          },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          { field: "dso_estimate", as: "dso_estimate", type: "number" },
        ],
      ),

      // 5. 90+ day condition
      n("5", "condition", "Any invoices 90+ days overdue?", 1000, {
        conditionData: [
          {
            variable: "has_90_plus",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Finance weekly report (email)
      emailNode(
        "6",
        "Finance: Weekly AR aging report",
        1240,
        "{finance_team_email}",
        "Weekly AR Aging Report — {risk_grade} Risk | DSO: {dso_estimate} days",
        "AR Aging Summary:\\n\\n{aging_summary}\\n\\nRisk Grade: {risk_grade}\\nEstimated DSO: {dso_estimate} days\\n\\n{risk_narrative}\\n\\nCollection Priority: {collection_priority}\\n\\nRecommended Actions:\\n{recommended_actions}\\n\\nCritical 90+ Customers:\\n{critical_customers}",
      ),

      // 7. Collection reminder trigger (True path — 90+ day invoices exist)
      emailNode(
        "7",
        "Collection reminder for 90+ day accounts",
        1480,
        "{collections_team_email}",
        "URGENT: {count_90_plus} invoices over 90 days — ${bucket_90_plus} outstanding",
        "The following accounts have invoices overdue by 90+ days totaling ${bucket_90_plus}:\\n\\n{critical_customers}\\n\\nRecommended action: {recommended_actions}\\n\\nPlease initiate formal collection procedures for these accounts immediately.",
        400,
      ),

      // 8. Aging snapshot logging (Sheets)
      sheetsNode(
        "8",
        "Log aging snapshot to Sheets",
        1720,
        "ARAgingSnapshot",
        '[["{total_outstanding}","{bucket_current}","{bucket_30}","{bucket_60}","{bucket_90}","{bucket_90_plus}","{count_90_plus}","{risk_grade}","{dso_estimate}","{collection_priority}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // 90+ invoices exist → collection reminder
      e("5", "6", "output_2"), // No 90+ → standard weekly report
      e("7", "6"), // After collection trigger → also send weekly report
      e("6", "8"), // Report → snapshot log
    ],
  },

  // ===================================================================
  // 601. ACCOUNTS RECEIVABLE AGING ALERT (7 nodes)
  // ===================================================================

  "Accounts Receivable Aging Alert": {
    summary:
      "Daily AR aging alert with AI risk classification and severity-based escalation. A daily threshold scan triggers invoice and customer data pull, AI risk classification by severity, condition-based routing for critical vs standard accounts, courtesy or manager or executive alert emails, escalation logging to Sheets, and duplicate suppression tracking.",
    nodes: [
      trigger("Schedule: Daily AR aging threshold scan", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs daily at 9 AM to scan for overdue invoices exceeding aging thresholds and trigger severity-based alerts",
        setup:
          "Configure Stripe API credentials. Set severity thresholds in the runCode node. Connect escalation email addresses.",
        inputFields: [],
      }),

      // 2. Invoice + customer pull
      stripeNode(
        "2",
        "Pull overdue invoices + customer data",
        280,
        "GET",
        "invoices?status=open&due_date[lt]={current_timestamp}&limit=100&expand[]=data.customer",
        [],
        [
          { name: "data", customName: "overdue_invoices", type: "string" },
          { name: "data.length", customName: "overdue_count", type: "number" },
        ],
      ),

      // 3. AI risk classification
      aiNode(
        "3",
        "AI classify overdue invoice risk",
        520,
        "You are an AR collections specialist. Analyze overdue invoices, classify each by severity (courtesy, standard, escalated, critical), and identify which accounts need immediate attention. Consider amount, days overdue, and customer relationship value. Respond JSON: {severity: string, critical_count: number, escalated_count: number, standard_count: number, courtesy_count: number, top_critical_accounts: string, total_overdue_amount: number, risk_summary: string, recommended_approach: string}",
        "Overdue invoices data: {overdue_invoices} | Total overdue: {overdue_count}",
        [
          { field: "severity", as: "overall_severity", type: "string" },
          { field: "critical_count", as: "critical_count", type: "number" },
          { field: "escalated_count", as: "escalated_count", type: "number" },
          { field: "standard_count", as: "standard_count", type: "number" },
          { field: "courtesy_count", as: "courtesy_count", type: "number" },
          {
            field: "top_critical_accounts",
            as: "top_critical_accounts",
            type: "string",
          },
          {
            field: "total_overdue_amount",
            as: "total_overdue_amount",
            type: "number",
          },
          { field: "risk_summary", as: "risk_summary", type: "string" },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
        ],
      ),

      // 4. Severity condition
      n("4", "condition", "Critical or escalated invoices?", 760, {
        conditionData: [
          {
            variable: "critical_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "OR",
          },
          {
            variable: "escalated_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "OR",
          },
        ],
      }),

      // 5. Manager/Executive alert (True path — critical/escalated)
      emailNode(
        "5",
        "Manager + Executive escalation alert",
        1000,
        "{finance_manager_email}",
        "🚨 AR ESCALATION: {critical_count} critical + {escalated_count} escalated overdue invoices",
        "AR Escalation Alert:\\n\\nOverall severity: {overall_severity}\\nCritical accounts: {critical_count} | Escalated: {escalated_count}\\nTotal overdue: ${total_overdue_amount}\\n\\nTop critical accounts:\\n{top_critical_accounts}\\n\\nRisk summary: {risk_summary}\\n\\nRecommended approach: {recommended_approach}\\n\\nImmediate action required.",
      ),

      // 5b. Courtesy alert (False path — standard/courtesy only)
      emailNode(
        "5b",
        "Courtesy reminder to collections team",
        1000,
        "{collections_team_email}",
        "AR Daily Update: {overdue_count} overdue invoices — ${total_overdue_amount}",
        "Daily AR Status:\\n\\nOverdue invoices: {overdue_count}\\nTotal overdue: ${total_overdue_amount}\\n\\nBreakdown: {courtesy_count} courtesy, {standard_count} standard\\n\\nRisk summary: {risk_summary}\\nRecommended approach: {recommended_approach}",
        400,
      ),

      // 6. Escalation logging (Sheets)
      sheetsNode(
        "6",
        "Log escalation to Sheets",
        1240,
        "AREscalation",
        '[["{overall_severity}","{critical_count}","{escalated_count}","{standard_count}","{courtesy_count}","{total_overdue_amount}","{top_critical_accounts}","{recommended_approach}","{overdue_count}"]]',
      ),

      // 7. Duplicate suppression tracking
      sheetsNode(
        "7",
        "Update duplicate suppression tracker",
        1480,
        "ARAlertTracker",
        '[["{top_critical_accounts}","{overall_severity}","{total_overdue_amount}","sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Critical/escalated → manager alert
      e("4", "5b", "output_2"), // Standard/courtesy → courtesy alert
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 602. ANNUAL REVENUE REPORT GENERATOR (8 nodes)
  // ===================================================================

  "Annual Revenue Report Generator": {
    summary:
      "Generates comprehensive annual revenue reports with AI YoY analysis for board presentation. A fiscal year trigger pulls full-year revenue from Stripe, fetches operating context, AI performs YoY analysis and generates an executive narrative, creates a board workbook in Google Sheets, securely distributes via email, and archives the report.",
    nodes: [
      trigger("Schedule: Fiscal year-end revenue report", "schedule", {
        schedule: "0 6 1 1 *",
        howItStarts:
          "Runs on January 1st (or fiscal year start) to generate the full annual revenue report for board distribution",
        setup:
          "Configure Stripe API credentials for revenue data. Set fiscal year dates. Connect Google Sheets for workbook creation and email distribution list for board members.",
        inputFields: [],
      }),

      // 2. Full-year revenue pull (Stripe)
      stripeNode(
        "2",
        "Pull full-year revenue from Stripe",
        280,
        "GET",
        "charges?created[gte]={fiscal_year_start}&created[lte]={fiscal_year_end}&limit=100",
        [],
        [
          { name: "data", customName: "annual_charges", type: "string" },
          {
            name: "data.length",
            customName: "total_transactions",
            type: "number",
          },
        ],
      ),

      // 3. Operating context pull
      n("3", "hitApi", "Pull operating context & prior year data", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{analytics_api}/api/v1/annual-summary?year={fiscal_year}&include=prior_year,segments,geo",
          headers: [
            { key: "Authorization", value: "Bearer {analytics_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.total_revenue",
              customName: "annual_revenue",
              type: "number",
            },
            {
              name: "data.prior_year_revenue",
              customName: "prior_year_revenue",
              type: "number",
            },
            {
              name: "data.segment_breakdown",
              customName: "segment_breakdown",
              type: "string",
            },
            {
              name: "data.geo_breakdown",
              customName: "geo_breakdown",
              type: "string",
            },
            {
              name: "data.customer_count",
              customName: "annual_customer_count",
              type: "number",
            },
            {
              name: "data.new_customer_revenue",
              customName: "new_customer_revenue",
              type: "number",
            },
            {
              name: "data.expansion_revenue",
              customName: "expansion_revenue",
              type: "number",
            },
            {
              name: "data.churned_revenue",
              customName: "churned_revenue",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI YoY analysis
      aiNode(
        "4",
        "AI analyze YoY revenue trends",
        760,
        "You are a CFO-level financial analyst preparing an annual revenue report for the board. Analyze year-over-year performance, segment trends, geographic distribution, and customer cohort dynamics. Provide strategic insights. Respond JSON: {yoy_growth_pct: number, executive_summary: string, key_highlights: string, risk_factors: string, growth_drivers: string, strategic_recommendations: string, revenue_quality_score: string}",
        "Annual revenue: ${annual_revenue} | Prior year: ${prior_year_revenue} | Segments: {segment_breakdown} | Geo: {geo_breakdown} | Customers: {annual_customer_count} | New revenue: ${new_customer_revenue} | Expansion: ${expansion_revenue} | Churned: ${churned_revenue} | Transactions: {total_transactions}",
        [
          { field: "yoy_growth_pct", as: "yoy_growth_pct", type: "number" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_highlights", as: "key_highlights", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          { field: "growth_drivers", as: "growth_drivers", type: "string" },
          {
            field: "strategic_recommendations",
            as: "strategic_recommendations",
            type: "string",
          },
          {
            field: "revenue_quality_score",
            as: "revenue_quality_score",
            type: "string",
          },
        ],
      ),

      // 5. Executive narrative generation
      aiNode(
        "5",
        "AI generate board-ready narrative",
        1000,
        "You are a corporate communications specialist. Write a polished, board-ready revenue narrative that combines financial data with strategic context. Include charts descriptions and section headers. Respond JSON: {board_narrative: string, one_page_summary: string, ceo_talking_points: string}",
        "YoY growth: {yoy_growth_pct}% | Executive summary: {executive_summary} | Highlights: {key_highlights} | Risks: {risk_factors} | Drivers: {growth_drivers} | Recommendations: {strategic_recommendations} | Revenue: ${annual_revenue} | Prior: ${prior_year_revenue} | Quality: {revenue_quality_score}",
        [
          { field: "board_narrative", as: "board_narrative", type: "string" },
          { field: "one_page_summary", as: "one_page_summary", type: "string" },
          {
            field: "ceo_talking_points",
            as: "ceo_talking_points",
            type: "string",
          },
        ],
      ),

      // 6. Board workbook creation (Sheets)
      sheetsNode(
        "6",
        "Create board revenue workbook (Sheets)",
        1240,
        "AnnualRevenue",
        '[["{annual_revenue}","{prior_year_revenue}","{yoy_growth_pct}","{segment_breakdown}","{geo_breakdown}","{annual_customer_count}","{new_customer_revenue}","{expansion_revenue}","{churned_revenue}","{revenue_quality_score}","{executive_summary}","{key_highlights}"]]',
      ),

      // 7. Secure distribution (email)
      emailNode(
        "7",
        "Distribute annual report to board",
        1480,
        "{board_distribution_list}",
        "Annual Revenue Report — {yoy_growth_pct}% YoY Growth | Revenue Quality: {revenue_quality_score}",
        "Dear Board Members,\\n\\nPlease find the annual revenue report attached.\\n\\n--- One-Page Summary ---\\n{one_page_summary}\\n\\n--- CEO Talking Points ---\\n{ceo_talking_points}\\n\\nThe full workbook with detailed segment and geographic breakdowns is available in Google Sheets.\\n\\nKey highlights: {key_highlights}\\n\\nBest regards,\\nFinance Team",
      ),

      // 8. Archive
      sheetsNode(
        "8",
        "Archive annual report metadata",
        1720,
        "ReportArchive",
        '[["{annual_revenue}","{yoy_growth_pct}","{revenue_quality_score}","{annual_customer_count}","{executive_summary}","distributed"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 603. AUTOMATED INVOICE SENDER (7 nodes)
  // ===================================================================

  "Automated Invoice Sender": {
    summary:
      "End-to-end automated invoicing from billing trigger to payment monitoring. A billing trigger pulls contract and line-item details, generates a Stripe invoice, emails the invoice with PDF link, logs to AR tracking in Sheets, monitors for payment, and triggers aging reminders for unpaid invoices.",
    nodes: [
      trigger("Webhook: Billing cycle or contract trigger", "webhook_app", {
        source: "CRM / Billing System",
        event: "billing.cycle_due",
        howItStarts:
          "A billing cycle begins or a new contract is activated, triggering automated invoice generation and delivery",
        setup:
          "Connect your CRM or billing system to POST billing events (customer ID, contract details, line items) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Contract/line-item pull via AI extraction
      aiNode(
        "2",
        "AI extract billing details",
        280,
        "You are a billing specialist. Extract customer billing details from this webhook payload regardless of format. Find customer ID, email, company name, contract ID, line items with descriptions and amounts, billing period, and payment terms. Respond JSON: {customer_id: string, customer_email: string, company_name: string, contract_id: string, line_items: string, total_amount: number, billing_period: string, payment_terms: string, currency: string}",
        "{webhook_payload}",
        [
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "contract_id", as: "contract_id", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "billing_period", as: "billing_period", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
          { field: "currency", as: "currency", type: "string" },
        ],
      ),

      // 3. Invoice generation (Stripe)
      stripeNode(
        "3",
        "Create invoice in Stripe",
        520,
        "POST",
        "invoices",
        [
          { key: "customer", value: "{customer_id}" },
          { key: "collection_method", value: "send_invoice" },
          { key: "days_until_due", value: "30" },
          {
            key: "description",
            value: "Invoice for {company_name} — {billing_period}",
          },
        ],
        [
          { name: "id", customName: "stripe_invoice_id", type: "string" },
          { name: "number", customName: "invoice_number", type: "string" },
          {
            name: "hosted_invoice_url",
            customName: "invoice_url",
            type: "string",
          },
          {
            name: "invoice_pdf",
            customName: "invoice_pdf_url",
            type: "string",
          },
          { name: "due_date", customName: "due_date", type: "number" },
        ],
      ),

      // 4. Invoice email with PDF
      emailNode(
        "4",
        "Send invoice to customer",
        760,
        "{customer_email}",
        "Invoice #{invoice_number} from OmniEngage — {billing_period}",
        "Dear {company_name},\\n\\nPlease find your invoice for {billing_period}.\\n\\nInvoice #: {invoice_number}\\nAmount: {currency} {total_amount}\\nPayment terms: {payment_terms}\\nContract: {contract_id}\\n\\nLine items:\\n{line_items}\\n\\nView invoice: {invoice_url}\\nDownload PDF: {invoice_pdf_url}\\n\\nThank you for your business.",
      ),

      // 5. AR logging (Sheets)
      sheetsNode(
        "5",
        "Log to AR tracker (Sheets)",
        1000,
        "ARTracker",
        '[["{stripe_invoice_id}","{invoice_number}","{company_name}","{customer_email}","{total_amount}","{currency}","{billing_period}","{payment_terms}","{due_date}","sent"]]',
      ),

      // 6. Payment monitoring delay
      n("6", "delayAction", "Wait for payment window", 1240, {
        delayDetails: { delayType: "fixed", delayValue: 7, delayUnit: "days" },
      }),

      // 7. Aging reminder trigger
      emailNode(
        "7",
        "Payment reminder for unpaid invoice",
        1480,
        "{customer_email}",
        "Reminder: Invoice #{invoice_number} — payment due soon",
        "Dear {company_name},\\n\\nThis is a friendly reminder that invoice #{invoice_number} for {currency} {total_amount} is approaching its due date.\\n\\nBilling period: {billing_period}\\nView invoice: {invoice_url}\\n\\nIf you have already submitted payment, please disregard this notice.\\n\\nThank you.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 604. BILLING DISPUTE RESOLUTION BOT (8 nodes)
  // ===================================================================

  "Billing Dispute Resolution Bot": {
    summary:
      "Handles billing disputes end-to-end with AI classification and automated resolution. A dispute trigger fetches invoice and payment history from Stripe, AI classifies the dispute type and resolution path, routes auto-resolvable disputes through a condition, processes refunds or credits, creates a CRM case in HubSpot, emails the customer a resolution, and logs root cause data to Sheets.",
    nodes: [
      trigger("Webhook: Billing dispute opened", "webhook_app", {
        source: "Stripe / Support System",
        event: "charge.dispute.created",
        howItStarts:
          "A customer opens a billing dispute or chargeback, triggering the automated resolution workflow",
        setup:
          "Connect Stripe dispute webhooks or your support system to POST dispute events to the workflow URL.",
        inputFields: [],
      }),

      // 2. Invoice/payment history pull (Stripe)
      aiNode(
        "2",
        "AI extract dispute details",
        280,
        "You are a billing dispute specialist. Extract all relevant details from this dispute webhook regardless of format. Find customer email, charge ID, dispute amount, dispute reason, original invoice number, and any customer statement. Respond JSON: {customer_email: string, customer_name: string, charge_id: string, dispute_amount: number, dispute_reason: string, invoice_number: string, customer_statement: string, dispute_id: string, currency: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "charge_id", as: "charge_id", type: "string" },
          { field: "dispute_amount", as: "dispute_amount", type: "number" },
          { field: "dispute_reason", as: "dispute_reason", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          {
            field: "customer_statement",
            as: "customer_statement",
            type: "string",
          },
          { field: "dispute_id", as: "dispute_id", type: "string" },
          { field: "currency", as: "currency", type: "string" },
        ],
      ),

      // 3. Payment history lookup
      stripeNode(
        "3",
        "Pull payment history for charge",
        520,
        "GET",
        "charges/{charge_id}?expand[]=invoice&expand[]=customer",
        [],
        [
          {
            name: "amount",
            customName: "original_charge_amount",
            type: "number",
          },
          {
            name: "invoice.lines.data",
            customName: "invoice_line_items",
            type: "string",
          },
          {
            name: "customer.email",
            customName: "stripe_customer_email",
            type: "string",
          },
          { name: "receipt_url", customName: "receipt_url", type: "string" },
          {
            name: "payment_method_details.type",
            customName: "payment_method",
            type: "string",
          },
        ],
      ),

      // 4. AI dispute classification
      aiNode(
        "4",
        "AI classify dispute & recommend resolution",
        760,
        "You are a billing dispute resolution expert. Classify this dispute and determine if it can be auto-resolved. Consider duplicate charges, service not received, product quality, subscription cancellation, and unauthorized charges. Respond JSON: {dispute_category: string, auto_resolvable: string, resolution_type: string, resolution_action: string, refund_recommended: string, refund_amount: number, evidence_summary: string, root_cause: string, prevention_suggestion: string}",
        "Dispute reason: {dispute_reason} | Amount: {currency} {dispute_amount} | Customer statement: {customer_statement} | Original charge: {original_charge_amount} | Invoice: {invoice_number} | Line items: {invoice_line_items} | Payment method: {payment_method}",
        [
          { field: "dispute_category", as: "dispute_category", type: "string" },
          { field: "auto_resolvable", as: "auto_resolvable", type: "string" },
          { field: "resolution_type", as: "resolution_type", type: "string" },
          {
            field: "resolution_action",
            as: "resolution_action",
            type: "string",
          },
          {
            field: "refund_recommended",
            as: "refund_recommended",
            type: "string",
          },
          { field: "refund_amount", as: "refund_amount", type: "number" },
          { field: "evidence_summary", as: "evidence_summary", type: "string" },
          { field: "root_cause", as: "root_cause", type: "string" },
          {
            field: "prevention_suggestion",
            as: "prevention_suggestion",
            type: "string",
          },
        ],
      ),

      // 5. Auto-resolve condition
      n("5", "condition", "Can be auto-resolved?", 1000, {
        conditionData: [
          {
            variable: "auto_resolvable",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Refund/credit processing (True path)
      stripeNode(
        "6",
        "Process refund/credit",
        1240,
        "POST",
        "refunds",
        [
          { key: "charge", value: "{charge_id}" },
          { key: "amount", value: "{refund_amount}" },
          { key: "reason", value: "requested_by_customer" },
        ],
        [
          { name: "id", customName: "refund_id", type: "string" },
          { name: "status", customName: "refund_status", type: "string" },
        ],
      ),

      // 6b. CRM case creation for manual review (False path)
      hubspotNode(
        "6b",
        "Create dispute case in HubSpot",
        1240,
        "POST",
        "crm/v3/objects/tickets",
        [
          {
            key: "properties",
            value:
              '{"subject":"Billing dispute: {dispute_category} — {customer_name}","content":"Dispute #{dispute_id} for {currency} {dispute_amount}\\nReason: {dispute_reason}\\nCategory: {dispute_category}\\nCustomer statement: {customer_statement}\\nEvidence: {evidence_summary}\\nResolution recommendation: {resolution_action}","hs_pipeline":"support","hs_ticket_priority":"HIGH"}',
          },
        ],
        [{ name: "id", customName: "ticket_id", type: "string" }],
        400,
      ),

      // 7. Customer resolution email
      emailNode(
        "7",
        "Email customer resolution update",
        1480,
        "{customer_email}",
        "Update on your billing dispute — Invoice #{invoice_number}",
        "Dear {customer_name},\\n\\nWe have reviewed your billing dispute regarding invoice #{invoice_number} for {currency} {dispute_amount}.\\n\\nDispute category: {dispute_category}\\nResolution: {resolution_action}\\n\\nReceipt: {receipt_url}\\n\\nIf you have any questions, please reply to this email and our team will assist you.\\n\\nBest regards,\\nBilling Team",
      ),

      // 8. Root cause logging (Sheets)
      sheetsNode(
        "8",
        "Log dispute root cause (Sheets)",
        1720,
        "DisputeRootCause",
        '[["{dispute_id}","{customer_email}","{dispute_amount}","{dispute_category}","{root_cause}","{resolution_type}","{auto_resolvable}","{refund_recommended}","{prevention_suggestion}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Auto-resolvable → process refund
      e("5", "6b", "output_2"), // Not auto-resolvable → CRM case
      e("6", "7"), // After refund → customer email
      e("6b", "7"), // After CRM case → customer email
      e("7", "8"), // Resolution → root cause log
    ],
  },

  // ===================================================================
  // 605. BILLING SUPPORT BOT (7 nodes)
  // ===================================================================

  "Billing Support Bot": {
    summary:
      "AI-powered billing support that answers customer billing questions with Stripe-grounded data. A billing message trigger pulls the customer billing profile from Stripe, AI generates a grounded answer, routes self-service actions, checks escalation conditions, creates Zendesk tickets for complex issues, and logs all interactions to Sheets.",
    nodes: [
      trigger("Webhook: Customer billing inquiry", "webhook_app", {
        source: "Chat Widget / Support Portal",
        event: "message.billing",
        howItStarts:
          "A customer submits a billing question via chat, support portal, or email, triggering the AI billing assistant",
        setup:
          "Connect your chat widget or support portal to POST customer messages with customer ID to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract message + customer identification
      aiNode(
        "2",
        "AI extract billing inquiry",
        280,
        "You are a billing support intake specialist. Parse this customer message regardless of format. Extract the customer identifier, the billing question or issue, and any specific invoice or charge references. Respond JSON: {customer_id: string, customer_email: string, question: string, issue_type: string, invoice_reference: string, urgency: string}",
        "{webhook_payload}",
        [
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "question", as: "customer_question", type: "string" },
          { field: "issue_type", as: "issue_type", type: "string" },
          {
            field: "invoice_reference",
            as: "invoice_reference",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. Billing profile pull (Stripe)
      stripeNode(
        "3",
        "Pull billing profile from Stripe",
        520,
        "GET",
        "customers/{customer_id}?expand[]=subscriptions&expand[]=invoices",
        [],
        [
          { name: "name", customName: "customer_name", type: "string" },
          {
            name: "subscriptions.data",
            customName: "subscription_details",
            type: "string",
          },
          {
            name: "invoices.data",
            customName: "recent_invoices",
            type: "string",
          },
          { name: "balance", customName: "account_balance", type: "number" },
          { name: "currency", customName: "currency", type: "string" },
        ],
      ),

      // 4. AI grounded answer
      aiNode(
        "4",
        "AI generate grounded billing answer",
        760,
        "You are a friendly, accurate billing support agent. Answer the customer question using ONLY the billing data provided. Never fabricate invoice numbers or amounts. If you cannot answer from the data, recommend escalation. Determine if the issue needs human review. Respond JSON: {answer: string, needs_escalation: string, self_service_action: string, confidence: string}",
        "Customer question: {customer_question} | Issue type: {issue_type} | Invoice ref: {invoice_reference} | Subscriptions: {subscription_details} | Recent invoices: {recent_invoices} | Balance: {account_balance} {currency} | Customer: {customer_name}",
        [
          { field: "answer", as: "bot_answer", type: "string" },
          { field: "needs_escalation", as: "needs_escalation", type: "string" },
          {
            field: "self_service_action",
            as: "self_service_action",
            type: "string",
          },
          { field: "confidence", as: "confidence", type: "string" },
        ],
      ),

      // 5. Escalation condition
      n("5", "condition", "Needs human escalation?", 1000, {
        conditionData: [
          {
            variable: "needs_escalation",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Zendesk ticket creation (True path)
      zendeskNode(
        "6",
        "Create Zendesk support ticket",
        1240,
        "Billing inquiry: {issue_type} — {customer_name}",
        "Customer: {customer_name} ({customer_email})\\nQuestion: {customer_question}\\nIssue type: {issue_type}\\nInvoice ref: {invoice_reference}\\nBot answer: {bot_answer}\\nConfidence: {confidence}\\nSelf-service action: {self_service_action}",
        "high",
        [{ name: "ticket.id", customName: "ticket_id", type: "string" }],
      ),

      // 7. Interaction logging (Sheets)
      sheetsNode(
        "7",
        "Log billing interaction (Sheets)",
        1480,
        "BillingInteractions",
        '[["{customer_email}","{customer_name}","{customer_question}","{issue_type}","{bot_answer}","{needs_escalation}","{confidence}","{self_service_action}","{urgency}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Needs escalation → Zendesk ticket
      e("5", "7", "output_2"), // No escalation → log directly
      e("6", "7"), // After ticket → log
    ],
  },

  // ===================================================================
  // 606. BUDGET ALERT BOT (7 nodes)
  // ===================================================================

  "Budget Alert Bot": {
    summary:
      "Conversational budget inquiry bot that responds to Slack queries with AI-powered burn rate analysis. A Slack query trigger pulls budget and spend data from Sheets, AI analyzes burn rate and forecasts overruns, routes through an overrun condition, notifies department heads via Slack, sends a conversational response, and logs queries to Sheets.",
    nodes: [
      trigger("Webhook: Slack budget inquiry", "webhook_app", {
        source: "Slack",
        event: "message.budget_query",
        howItStarts:
          "A team member asks a budget-related question in Slack (e.g., 'How much is left in the marketing budget?'), triggering real-time analysis",
        setup:
          "Configure a Slack bot to forward budget-related messages (matching keywords like 'budget', 'spend', 'remaining') to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract query
      aiNode(
        "2",
        "AI parse budget query",
        280,
        "You are a budget assistant. Parse this Slack message to identify the department, budget category, time period, and specific question being asked. Respond JSON: {department: string, category: string, time_period: string, question: string, slack_user: string, slack_channel: string}",
        "{webhook_payload}",
        [
          { field: "department", as: "department", type: "string" },
          { field: "category", as: "budget_category", type: "string" },
          { field: "time_period", as: "time_period", type: "string" },
          { field: "question", as: "budget_question", type: "string" },
          { field: "slack_user", as: "slack_user", type: "string" },
          { field: "slack_channel", as: "slack_channel", type: "string" },
        ],
      ),

      // 3. Budget/spend pull (Sheets)
      n("3", "hitApi", "Pull budget & spend data (Sheets)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{budget_sheet_id}/values/{department}Budget!A1:Z100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "budget_data", type: "string" },
          ],
        },
      }),

      // 4. AI burn rate analysis
      aiNode(
        "4",
        "AI analyze burn rate & forecast",
        760,
        "You are a financial analyst. Analyze budget vs actual spend data for the requested department and period. Calculate burn rate, project end-of-period spend, and identify any overrun risks. Provide a conversational answer to the user's question. Respond JSON: {answer: string, total_budget: number, total_spent: number, remaining: number, burn_rate_pct: number, projected_overrun: string, overrun_amount: number, risk_level: string}",
        "Department: {department} | Category: {budget_category} | Period: {time_period} | Question: {budget_question} | Budget data: {budget_data}",
        [
          { field: "answer", as: "budget_answer", type: "string" },
          { field: "total_budget", as: "total_budget", type: "number" },
          { field: "total_spent", as: "total_spent", type: "number" },
          { field: "remaining", as: "remaining_budget", type: "number" },
          { field: "burn_rate_pct", as: "burn_rate_pct", type: "number" },
          {
            field: "projected_overrun",
            as: "projected_overrun",
            type: "string",
          },
          { field: "overrun_amount", as: "overrun_amount", type: "number" },
          { field: "risk_level", as: "risk_level", type: "string" },
        ],
      ),

      // 5. Overrun condition
      n("5", "condition", "Budget overrun projected?", 1000, {
        conditionData: [
          {
            variable: "projected_overrun",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Department head Slack notification (True path) + conversational response
      slackNode(
        "6",
        "Alert department head on overrun",
        1240,
        "⚠️ *Budget Overrun Alert — {department}*\\nBudget: ${total_budget} | Spent: ${total_spent} | Remaining: ${remaining_budget}\\nBurn rate: {burn_rate_pct}% | Projected overrun: ${overrun_amount}\\nRisk: {risk_level}\\n\\nTriggered by @{slack_user}'s inquiry in #{slack_channel}",
      ),

      // 6b. Conversational response (both paths)
      slackNode(
        "6b",
        "Reply to budget query in Slack",
        1240,
        "📊 *Budget Update for {department}*\\n{budget_answer}\\n\\nBudget: ${total_budget} | Spent: ${total_spent} | Remaining: ${remaining_budget}\\nBurn rate: {burn_rate_pct}%",
        400,
      ),

      // 7. Query logging (Sheets)
      sheetsNode(
        "7",
        "Log budget query (Sheets)",
        1480,
        "BudgetQueries",
        '[["{slack_user}","{department}","{budget_question}","{total_budget}","{total_spent}","{remaining_budget}","{burn_rate_pct}","{projected_overrun}","{risk_level}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Overrun → department head alert
      e("5", "6b", "output_2"), // No overrun → reply directly
      e("6", "6b"), // After alert → also reply
      e("6b", "7"), // Reply → log
    ],
  },

  // ===================================================================
  // 607. BUDGET ALERT MONITOR (7 nodes)
  // ===================================================================

  "Budget Alert Monitor": {
    summary:
      "Proactive daily budget monitoring with automated alerts for all departments. A daily schedule pulls all department budgets, runCode computes burn rates and thresholds, routes through a condition for departments exceeding limits, sends Slack alerts to finance and department heads, emails a weekly summary, and updates the budget dashboard in Sheets.",
    nodes: [
      trigger("Schedule: Daily budget monitoring", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs daily at 8 AM to scan all department budgets, compute burn rates, and alert on threshold breaches",
        setup:
          "Configure Google Sheets with budget data for all departments. Set threshold percentages in the runCode node.",
        inputFields: [],
      }),

      // 2. All department budgets pull
      n("2", "hitApi", "Pull all department budgets (Sheets)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{budget_sheet_id}/values/AllBudgets!A1:Z100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "all_budgets_data", type: "string" },
          ],
        },
      }),

      // 3. Burn rate computation (runCode)
      n("3", "runCode", "Compute burn rates & flag breaches", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute burn rates for all departments and flag threshold breaches
const budgetRows = JSON.parse(vars.all_budgets_data || "[]");
const thresholdPct = 80; // Alert when 80% consumed
const now = new Date();
const dayOfMonth = now.getDate();
const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
const expectedBurnPct = (dayOfMonth / daysInMonth) * 100;

let alertDepartments = [];
let totalBudget = 0;
let totalSpent = 0;
let breachCount = 0;
let summaryLines = [];

budgetRows.forEach((row, i) => {
  if (i === 0 || !row[0]) return; // Skip header
  const dept = row[0];
  const budget = parseFloat(row[1] || "0");
  const spent = parseFloat(row[2] || "0");
  const burnPct = budget > 0 ? (spent / budget * 100) : 0;
  totalBudget += budget;
  totalSpent += spent;

  if (burnPct >= thresholdPct) {
    breachCount++;
    alertDepartments.push(dept + ": " + burnPct.toFixed(1) + "% consumed ($" + spent.toFixed(0) + "/$" + budget.toFixed(0) + ")");
  }
  summaryLines.push(dept + ": " + burnPct.toFixed(1) + "%");
});

const overallBurn = totalBudget > 0 ? (totalSpent / totalBudget * 100) : 0;
const hasBreaches = breachCount > 0 ? "yes" : "no";

return {
  has_breaches: hasBreaches,
  breach_count: breachCount,
  alert_departments: alertDepartments.join("; ") || "None",
  total_budget: totalBudget.toFixed(2),
  total_spent: totalSpent.toFixed(2),
  overall_burn_pct: overallBurn.toFixed(1),
  expected_burn_pct: expectedBurnPct.toFixed(1),
  department_summary: summaryLines.join("; "),
  budget_health: overallBurn > expectedBurnPct + 10 ? "at_risk" : "healthy"
};`,
          outputVariables: [
            { name: "has_breaches", type: "string" },
            { name: "breach_count", type: "number" },
            { name: "alert_departments", type: "string" },
            { name: "total_budget", type: "string" },
            { name: "total_spent", type: "string" },
            { name: "overall_burn_pct", type: "string" },
            { name: "expected_burn_pct", type: "string" },
            { name: "department_summary", type: "string" },
            { name: "budget_health", type: "string" },
          ],
        },
      }),

      // 4. Threshold condition
      n("4", "condition", "Any departments over threshold?", 760, {
        conditionData: [
          {
            variable: "has_breaches",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Finance + department alerts (Slack) — True path
      slackNode(
        "5",
        "Slack alert: Budget threshold breaches",
        1000,
        "🚨 *Budget Alert: {breach_count} Department(s) Over Threshold*\\nOverall: {overall_burn_pct}% consumed (expected: {expected_burn_pct}%)\\nHealth: {budget_health}\\n\\n*Breached departments:*\\n{alert_departments}\\n\\nTotal budget: ${total_budget} | Total spent: ${total_spent}",
      ),

      // 6. Weekly summary (email)
      emailNode(
        "6",
        "Finance: Weekly budget summary",
        1240,
        "{finance_team_email}",
        "Budget Monitor — {budget_health} | {overall_burn_pct}% consumed | {breach_count} alerts",
        "Weekly Budget Summary:\\n\\nOverall health: {budget_health}\\nTotal budget: ${total_budget}\\nTotal spent: ${total_spent}\\nBurn rate: {overall_burn_pct}% (expected: {expected_burn_pct}%)\\n\\nDepartment breakdown:\\n{department_summary}\\n\\nThreshold breaches ({breach_count}):\\n{alert_departments}",
      ),

      // 7. Budget dashboard (Sheets)
      sheetsNode(
        "7",
        "Update budget dashboard (Sheets)",
        1480,
        "BudgetDashboard",
        '[["{total_budget}","{total_spent}","{overall_burn_pct}","{expected_burn_pct}","{budget_health}","{breach_count}","{alert_departments}","{department_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Breaches → Slack alert
      e("4", "6", "output_2"), // No breaches → weekly summary
      e("5", "6"), // After alert → also send summary
      e("6", "7"), // Summary → dashboard
    ],
  },

  // ===================================================================
  // 608. BUDGET REALLOCATION REQUEST BOT (8 nodes)
  // ===================================================================

  "Budget Reallocation Request Bot": {
    summary:
      "Manages budget reallocation requests with validation, AI impact analysis, and Slack-based approvals. A Slack request trigger validates source and destination budgets via runCode, AI analyzes the impact, routes for approval, sends a Slack approval card, updates balances in Sheets, notifies stakeholders, and logs the audit trail.",
    nodes: [
      trigger("Webhook: Slack budget reallocation request", "webhook_app", {
        source: "Slack",
        event: "message.budget_reallocation",
        howItStarts:
          "A team member requests a budget reallocation via Slack (e.g., 'Move $5,000 from Marketing to Engineering'), triggering the validation and approval workflow",
        setup:
          "Configure a Slack bot to forward reallocation requests to the workflow URL. Ensure Google Sheets budget tracker is connected.",
        inputFields: [],
      }),

      // 2. AI extract request details
      aiNode(
        "2",
        "AI parse reallocation request",
        280,
        "You are a budget operations assistant. Parse this Slack message to extract the reallocation details regardless of format. Find the source department, destination department, amount, reason, and requestor. Respond JSON: {source_dept: string, dest_dept: string, amount: number, reason: string, requestor_name: string, requestor_email: string, slack_user: string, currency: string}",
        "{webhook_payload}",
        [
          { field: "source_dept", as: "source_dept", type: "string" },
          { field: "dest_dept", as: "dest_dept", type: "string" },
          { field: "amount", as: "realloc_amount", type: "number" },
          { field: "reason", as: "realloc_reason", type: "string" },
          { field: "requestor_name", as: "requestor_name", type: "string" },
          { field: "requestor_email", as: "requestor_email", type: "string" },
          { field: "slack_user", as: "slack_user", type: "string" },
          { field: "currency", as: "currency", type: "string" },
        ],
      ),

      // 3. Source/destination validation (runCode)
      n("3", "runCode", "Validate source & destination budgets", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate that the source department has sufficient remaining budget
const amount = parseFloat(vars.realloc_amount || "0");
const sourceDept = vars.source_dept || "Unknown";
const destDept = vars.dest_dept || "Unknown";

// These would come from a Sheets lookup in production
const sourceRemaining = 25000; // placeholder — actual comes from budget data
const destBudget = 50000;

const valid = amount > 0 && amount <= sourceRemaining && sourceDept !== destDept;
const sourceAfter = sourceRemaining - amount;
const destAfter = destBudget + amount;

return {
  validation_passed: valid ? "yes" : "no",
  validation_message: valid
    ? sourceDept + " has $" + sourceRemaining + " remaining. Transfer of $" + amount + " is feasible."
    : "Validation failed: " + (amount > sourceRemaining ? "Insufficient funds in " + sourceDept : amount <= 0 ? "Invalid amount" : "Same department"),
  source_remaining: sourceRemaining,
  source_after_transfer: sourceAfter,
  dest_after_transfer: destAfter,
  transfer_summary: "$" + amount + " from " + sourceDept + " to " + destDept
};`,
          outputVariables: [
            { name: "validation_passed", type: "string" },
            { name: "validation_message", type: "string" },
            { name: "source_remaining", type: "number" },
            { name: "source_after_transfer", type: "number" },
            { name: "dest_after_transfer", type: "number" },
            { name: "transfer_summary", type: "string" },
          ],
        },
      }),

      // 4. AI impact analysis
      aiNode(
        "4",
        "AI analyze reallocation impact",
        760,
        "You are a financial planning analyst. Assess the impact of this budget reallocation on both departments. Consider remaining runway, project commitments, and fiscal year implications. Respond JSON: {impact_assessment: string, risk_rating: string, approval_recommendation: string, conditions: string}",
        "Transfer: {transfer_summary} | Reason: {realloc_reason} | Validation: {validation_message} | Source remaining after: ${source_after_transfer} | Dest after: ${dest_after_transfer} | Requestor: {requestor_name}",
        [
          {
            field: "impact_assessment",
            as: "impact_assessment",
            type: "string",
          },
          { field: "risk_rating", as: "risk_rating", type: "string" },
          {
            field: "approval_recommendation",
            as: "approval_recommendation",
            type: "string",
          },
          { field: "conditions", as: "approval_conditions", type: "string" },
        ],
      ),

      // 5. Slack approval card
      slackNode(
        "5",
        "Slack: Reallocation approval request",
        1000,
        "📋 *Budget Reallocation Request*\\nFrom: {source_dept} → {dest_dept}\\nAmount: {currency} {realloc_amount}\\nRequestor: {requestor_name}\\nReason: {realloc_reason}\\n\\n*Validation:* {validation_message}\\n*Impact:* {impact_assessment}\\n*Risk:* {risk_rating}\\n*Recommendation:* {approval_recommendation}\\n*Conditions:* {approval_conditions}\\n\\nReact ✅ to approve or ❌ to reject.",
      ),

      // 6. Balance update (Sheets)
      sheetsNode(
        "6",
        "Update budget balances (Sheets)",
        1240,
        "BudgetBalances",
        '[["{source_dept}","-{realloc_amount}","{source_after_transfer}","{dest_dept}","+{realloc_amount}","{dest_after_transfer}","{realloc_reason}","{requestor_name}"]]',
      ),

      // 7. Stakeholder notification
      emailNode(
        "7",
        "Notify stakeholders of reallocation",
        1480,
        "{requestor_email}",
        "Budget reallocation processed: {transfer_summary}",
        "Hi {requestor_name},\\n\\nYour budget reallocation request has been processed.\\n\\nTransfer: {transfer_summary}\\nReason: {realloc_reason}\\n\\nSource ({source_dept}) remaining: {currency} {source_after_transfer}\\nDestination ({dest_dept}) new balance: {currency} {dest_after_transfer}\\n\\nImpact assessment: {impact_assessment}\\nConditions: {approval_conditions}\\n\\nFinance Team",
      ),

      // 8. Audit trail logging
      sheetsNode(
        "8",
        "Log reallocation audit trail",
        1720,
        "ReallocationAudit",
        '[["{source_dept}","{dest_dept}","{realloc_amount}","{realloc_reason}","{requestor_name}","{validation_passed}","{risk_rating}","{approval_recommendation}","{impact_assessment}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 609. BUDGET REQUEST SUBMISSION BOT (7 nodes)
  // ===================================================================

  "Budget Request Submission Bot": {
    summary:
      "Streamlines budget request intake with AI-structured processing and approval routing. A budget request trigger captures structured intake via AI, pulls historical utilization from Sheets, validates with runCode, creates a request record, notifies the approver via Slack, and sends a confirmation to the requester.",
    nodes: [
      trigger("Webhook: Budget request submitted", "webhook_app", {
        source: "Internal Portal / Slack",
        event: "budget.request_submitted",
        howItStarts:
          "An employee submits a budget request through the internal portal or Slack, triggering structured intake and approval routing",
        setup:
          "Connect your budget request portal or Slack bot to POST request details to the workflow URL.",
        inputFields: [],
      }),

      // 2. Structured intake (AI)
      aiNode(
        "2",
        "AI structure budget request",
        280,
        "You are a budget intake specialist. Parse this budget request regardless of format and structure it for processing. Extract department, amount, category (capex/opex), justification, timeline, and requestor details. Respond JSON: {department: string, amount: number, category: string, justification: string, timeline: string, requestor_name: string, requestor_email: string, priority: string, budget_line: string}",
        "{webhook_payload}",
        [
          { field: "department", as: "department", type: "string" },
          { field: "amount", as: "request_amount", type: "number" },
          { field: "category", as: "expense_category", type: "string" },
          { field: "justification", as: "justification", type: "string" },
          { field: "timeline", as: "timeline", type: "string" },
          { field: "requestor_name", as: "requestor_name", type: "string" },
          { field: "requestor_email", as: "requestor_email", type: "string" },
          { field: "priority", as: "priority", type: "string" },
          { field: "budget_line", as: "budget_line", type: "string" },
        ],
      ),

      // 3. Historical utilization pull (Sheets)
      n("3", "hitApi", "Pull historical budget utilization (Sheets)", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{budget_sheet_id}/values/{department}History!A1:Z50",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "historical_data", type: "string" },
          ],
        },
      }),

      // 4. Validation (runCode)
      n("4", "runCode", "Validate request against policy", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate budget request against policies
const amount = parseFloat(vars.request_amount || "0");
const category = vars.expense_category || "unknown";
const department = vars.department || "Unknown";
const priority = vars.priority || "normal";

// Policy checks
const maxAutoApproval = 5000;
const requiresFinanceReview = amount > 25000;
const requiresVPApproval = amount > 50000;

let approverLevel = "manager";
if (requiresVPApproval) approverLevel = "vp";
else if (requiresFinanceReview) approverLevel = "finance";
else if (amount <= maxAutoApproval) approverLevel = "auto";

const requestId = "BR-" + Date.now().toString(36).toUpperCase();
const valid = amount > 0 && department !== "Unknown";

return {
  request_id: requestId,
  validation_passed: valid ? "yes" : "no",
  approver_level: approverLevel,
  requires_finance: requiresFinanceReview ? "yes" : "no",
  auto_approvable: amount <= maxAutoApproval ? "yes" : "no",
  policy_notes: approverLevel === "auto" ? "Under $5K — eligible for auto-approval" : "Requires " + approverLevel + " approval ($" + amount + ")",
  request_summary: requestId + ": $" + amount + " " + category + " for " + department + " (" + priority + " priority)"
};`,
          outputVariables: [
            { name: "request_id", type: "string" },
            { name: "validation_passed", type: "string" },
            { name: "approver_level", type: "string" },
            { name: "requires_finance", type: "string" },
            { name: "auto_approvable", type: "string" },
            { name: "policy_notes", type: "string" },
            { name: "request_summary", type: "string" },
          ],
        },
      }),

      // 5. Request record creation (Sheets)
      sheetsNode(
        "5",
        "Create budget request record",
        1000,
        "BudgetRequests",
        '[["{request_id}","{department}","{request_amount}","{expense_category}","{justification}","{timeline}","{requestor_name}","{priority}","{approver_level}","pending"]]',
      ),

      // 6. Approver notification (Slack)
      slackNode(
        "6",
        "Notify approver via Slack",
        1240,
        "📋 *New Budget Request: {request_id}*\\nDepartment: {department} | Amount: ${request_amount}\\nCategory: {expense_category} | Priority: {priority}\\nRequestor: {requestor_name}\\nJustification: {justification}\\nTimeline: {timeline}\\n\\n{policy_notes}\\n\\nApproval level: {approver_level}",
      ),

      // 7. Confirmation to requester
      emailNode(
        "7",
        "Confirm request to requester",
        1480,
        "{requestor_email}",
        "Budget request {request_id} submitted — awaiting {approver_level} approval",
        "Hi {requestor_name},\\n\\nYour budget request has been submitted and is being routed for approval.\\n\\nRequest ID: {request_id}\\nDepartment: {department}\\nAmount: ${request_amount}\\nCategory: {expense_category}\\nPriority: {priority}\\nTimeline: {timeline}\\n\\nApproval level required: {approver_level}\\n{policy_notes}\\n\\nYou will be notified once a decision is made.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 610. BUDGET VS ACTUAL VARIANCE ALERT (8 nodes)
  // ===================================================================

  "Budget vs Actual Variance Alert": {
    summary:
      "Monthly budget vs actual variance analysis with AI-powered remediation suggestions. A monthly schedule pulls budget and actuals from Sheets, runCode calculates variance with significance testing, routes through a condition for material variances, AI generates remediation suggestions, alerts budget owners via Slack, updates the variance dashboard, and logs YTD trends.",
    nodes: [
      trigger("Schedule: Monthly variance analysis", "schedule", {
        schedule: "0 7 1 * *",
        howItStarts:
          "Runs on the 1st of each month to compare budget vs actual spend, identify significant variances, and alert budget owners",
        setup:
          "Configure Google Sheets with budget and actuals data. Set variance thresholds in the runCode node.",
        inputFields: [],
      }),

      // 2. Budget + actuals pull (Sheets)
      n("2", "hitApi", "Pull budget & actuals data (Sheets)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{budget_sheet_id}/values/BudgetActuals!A1:Z100",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "values",
              customName: "budget_actuals_data",
              type: "string",
            },
          ],
        },
      }),

      // 3. Variance calculation (runCode)
      n("3", "runCode", "Calculate variance & significance", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate budget vs actual variance with significance testing
const rows = JSON.parse(vars.budget_actuals_data || "[]");
const significanceThreshold = 10; // 10% variance is significant
let significantVariances = [];
let totalBudget = 0;
let totalActual = 0;
let largestOverspend = { dept: "", pct: 0 };
let largestUnderspend = { dept: "", pct: 0 };

rows.forEach((row, i) => {
  if (i === 0 || !row[0]) return; // skip header
  const dept = row[0];
  const budget = parseFloat(row[1] || "0");
  const actual = parseFloat(row[2] || "0");
  const variance = actual - budget;
  const variancePct = budget > 0 ? (variance / budget * 100) : 0;

  totalBudget += budget;
  totalActual += actual;

  if (Math.abs(variancePct) >= significanceThreshold) {
    significantVariances.push(dept + ": " + (variancePct > 0 ? "+" : "") + variancePct.toFixed(1) + "% ($" + variance.toFixed(0) + ")");
  }
  if (variancePct > largestOverspend.pct) largestOverspend = { dept, pct: variancePct };
  if (variancePct < largestUnderspend.pct) largestUnderspend = { dept, pct: variancePct };
});

const totalVariance = totalActual - totalBudget;
const totalVariancePct = totalBudget > 0 ? (totalVariance / totalBudget * 100) : 0;
const hasSignificant = significantVariances.length > 0 ? "yes" : "no";

return {
  has_significant_variances: hasSignificant,
  significant_count: significantVariances.length,
  significant_variances: significantVariances.join("; ") || "None",
  total_budget: totalBudget.toFixed(2),
  total_actual: totalActual.toFixed(2),
  total_variance: totalVariance.toFixed(2),
  total_variance_pct: totalVariancePct.toFixed(1),
  largest_overspend: largestOverspend.dept + " (" + largestOverspend.pct.toFixed(1) + "%)",
  largest_underspend: largestUnderspend.dept + " (" + largestUnderspend.pct.toFixed(1) + "%)"
};`,
          outputVariables: [
            { name: "has_significant_variances", type: "string" },
            { name: "significant_count", type: "number" },
            { name: "significant_variances", type: "string" },
            { name: "total_budget", type: "string" },
            { name: "total_actual", type: "string" },
            { name: "total_variance", type: "string" },
            { name: "total_variance_pct", type: "string" },
            { name: "largest_overspend", type: "string" },
            { name: "largest_underspend", type: "string" },
          ],
        },
      }),

      // 4. Significance condition
      n("4", "condition", "Significant variances detected?", 760, {
        conditionData: [
          {
            variable: "has_significant_variances",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI remediation suggestions (True path)
      aiNode(
        "5",
        "AI suggest variance remediation",
        1000,
        "You are a financial controller. Analyze budget variances and suggest specific, actionable remediation strategies for each department with significant variance. Respond JSON: {remediation_plan: string, priority_actions: string, risk_assessment: string, forecast_adjustment: string}",
        "Significant variances: {significant_variances} | Total variance: ${total_variance} ({total_variance_pct}%) | Largest overspend: {largest_overspend} | Largest underspend: {largest_underspend} | Budget: ${total_budget} | Actual: ${total_actual}",
        [
          { field: "remediation_plan", as: "remediation_plan", type: "string" },
          { field: "priority_actions", as: "priority_actions", type: "string" },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          {
            field: "forecast_adjustment",
            as: "forecast_adjustment",
            type: "string",
          },
        ],
      ),

      // 6. Budget owner alerts (Slack)
      slackNode(
        "6",
        "Slack: Variance alerts to budget owners",
        1240,
        "📊 *Monthly Budget Variance Report*\\nTotal: ${total_actual} vs ${total_budget} ({total_variance_pct}%)\\n\\n*Significant Variances ({significant_count}):*\\n{significant_variances}\\n\\n*Remediation:*\\n{remediation_plan}\\n\\n*Priority Actions:*\\n{priority_actions}",
      ),

      // 7. Variance dashboard update (Sheets)
      sheetsNode(
        "7",
        "Update variance dashboard (Sheets)",
        1480,
        "VarianceDashboard",
        '[["{total_budget}","{total_actual}","{total_variance}","{total_variance_pct}","{significant_count}","{significant_variances}","{largest_overspend}","{largest_underspend}","{remediation_plan}"]]',
      ),

      // 8. YTD trend logging
      sheetsNode(
        "8",
        "Log YTD variance trend",
        1720,
        "VarianceTrend",
        '[["{total_budget}","{total_actual}","{total_variance_pct}","{significant_count}","{risk_assessment}","{forecast_adjustment}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Significant → AI remediation
      e("4", "7", "output_2"), // Not significant → dashboard update
      e("5", "6"), // Remediation → Slack alert
      e("6", "7"), // Alert → dashboard
      e("7", "8"), // Dashboard → trend log
    ],
  },

  // ===================================================================
  // 611. CASH FLOW PROJECTION BOT (8 nodes)
  // ===================================================================

  "Cash Flow Projection Bot": {
    summary:
      "Multi-source cash flow forecasting with scenario modeling and negative cash week alerts. A scheduled or on-demand trigger pulls cash reserves, AR with collection likelihood, AP commitments, runs scenario modeling via runCode, AI generates a forecast narrative, creates a forecast workbook in Sheets, and sends Slack alerts for projected negative cash weeks.",
    nodes: [
      trigger("Schedule: Weekly cash flow projection", "schedule", {
        schedule: "0 7 * * 1",
        howItStarts:
          "Runs every Monday at 7 AM to project 13-week cash flow based on current positions, receivables, and payables",
        setup:
          "Configure bank API credentials for cash positions, Stripe for receivables, and ERP for payables. Connect Google Sheets for forecast output.",
        inputFields: [],
      }),

      // 2. Cash + reserves pull
      n("2", "hitApi", "Pull cash positions & reserves", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{banking_api}/api/v1/accounts/balances?include=cash,reserves,credit_facilities",
          headers: [
            { key: "Authorization", value: "Bearer {banking_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.total_cash",
              customName: "total_cash",
              type: "number",
            },
            {
              name: "data.operating_cash",
              customName: "operating_cash",
              type: "number",
            },
            { name: "data.reserves", customName: "reserves", type: "number" },
            {
              name: "data.credit_available",
              customName: "credit_available",
              type: "number",
            },
          ],
        },
      }),

      // 3. AR with collection likelihood pull
      stripeNode(
        "3",
        "Pull AR receivables pipeline",
        520,
        "GET",
        "invoices?status=open&limit=100&expand[]=data.customer",
        [],
        [
          { name: "data", customName: "open_receivables", type: "string" },
          {
            name: "data.length",
            customName: "open_invoice_count",
            type: "number",
          },
        ],
      ),

      // 4. AP + commitments pull
      n("4", "hitApi", "Pull AP obligations & commitments", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://{erp_api}/api/v1/payables/upcoming?weeks=13&include=recurring,one_time",
          headers: [
            { key: "Authorization", value: "Bearer {erp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.total_payables",
              customName: "total_payables",
              type: "number",
            },
            {
              name: "data.weekly_recurring",
              customName: "weekly_recurring_cost",
              type: "number",
            },
            {
              name: "data.upcoming_large_payments",
              customName: "large_payments",
              type: "string",
            },
            {
              name: "data.payroll_obligations",
              customName: "payroll_obligations",
              type: "number",
            },
          ],
        },
      }),

      // 5. Scenario modeling (runCode)
      n("5", "runCode", "13-week cash flow scenario model", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// 13-week cash flow projection with scenarios
const cash = parseFloat(vars.total_cash || "0");
const reserves = parseFloat(vars.reserves || "0");
const credit = parseFloat(vars.credit_available || "0");
const weeklyRecurring = parseFloat(vars.weekly_recurring_cost || "0");
const payroll = parseFloat(vars.payroll_obligations || "0");
const totalPayables = parseFloat(vars.total_payables || "0");
const invoiceCount = parseInt(vars.open_invoice_count || "0", 10);

// Estimate weekly collections (conservative: 60% of AR / 13 weeks)
const estimatedAR = invoiceCount * 2500; // rough average
const weeklyCollections = (estimatedAR * 0.6) / 13;
const weeklyOutflow = weeklyRecurring + (payroll / 4);

let runningCash = cash;
let negativeWeeks = [];
let weeklyProjections = [];

for (let week = 1; week <= 13; week++) {
  runningCash += weeklyCollections - weeklyOutflow;
  weeklyProjections.push("W" + week + ": $" + Math.round(runningCash));
  if (runningCash < 0) negativeWeeks.push("Week " + week + ": $" + Math.round(runningCash));
}

const hasNegativeWeeks = negativeWeeks.length > 0 ? "yes" : "no";
const lowestPoint = Math.min(cash, ...weeklyProjections.map((_, i) => cash + (weeklyCollections - weeklyOutflow) * (i + 1)));
const weeksOfRunway = weeklyOutflow > 0 ? Math.floor(cash / weeklyOutflow) : 99;

return {
  has_negative_weeks: hasNegativeWeeks,
  negative_week_count: negativeWeeks.length,
  negative_week_details: negativeWeeks.join("; ") || "None",
  week_13_balance: Math.round(runningCash),
  lowest_cash_point: Math.round(lowestPoint),
  weekly_inflow: Math.round(weeklyCollections),
  weekly_outflow: Math.round(weeklyOutflow),
  weeks_of_runway: weeksOfRunway,
  projection_summary: weeklyProjections.join(" | "),
  starting_cash: Math.round(cash)
};`,
          outputVariables: [
            { name: "has_negative_weeks", type: "string" },
            { name: "negative_week_count", type: "number" },
            { name: "negative_week_details", type: "string" },
            { name: "week_13_balance", type: "number" },
            { name: "lowest_cash_point", type: "number" },
            { name: "weekly_inflow", type: "number" },
            { name: "weekly_outflow", type: "number" },
            { name: "weeks_of_runway", type: "number" },
            { name: "projection_summary", type: "string" },
            { name: "starting_cash", type: "number" },
          ],
        },
      }),

      // 6. AI forecast narrative
      aiNode(
        "6",
        "AI generate cash flow narrative",
        1240,
        "You are a treasury analyst. Write a clear cash flow forecast narrative for executive review. Highlight key risks, suggest mitigations, and provide confidence levels for the projection. Respond JSON: {forecast_narrative: string, risk_factors: string, mitigation_actions: string, confidence_level: string, executive_summary: string}",
        "Starting cash: ${starting_cash} | Weekly inflow: ${weekly_inflow} | Weekly outflow: ${weekly_outflow} | Week 13 balance: ${week_13_balance} | Lowest point: ${lowest_cash_point} | Runway: {weeks_of_runway} weeks | Negative weeks: {negative_week_details} | Reserves: ${reserves} | Credit: ${credit_available} | Payroll: ${payroll_obligations}",
        [
          {
            field: "forecast_narrative",
            as: "forecast_narrative",
            type: "string",
          },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "mitigation_actions",
            as: "mitigation_actions",
            type: "string",
          },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
        ],
      ),

      // 7. Forecast workbook (Sheets)
      sheetsNode(
        "7",
        "Create cash flow forecast (Sheets)",
        1480,
        "CashFlowForecast",
        '[["{starting_cash}","{weekly_inflow}","{weekly_outflow}","{week_13_balance}","{lowest_cash_point}","{weeks_of_runway}","{negative_week_count}","{confidence_level}","{executive_summary}","{projection_summary}"]]',
      ),

      // 8. Negative cash week alert (Slack)
      slackNode(
        "8",
        "Slack: Cash flow risk alert",
        1720,
        "💰 *Cash Flow Projection — {confidence_level} Confidence*\\nStarting: ${starting_cash} | Week 13: ${week_13_balance}\\nRunway: {weeks_of_runway} weeks | Lowest: ${lowest_cash_point}\\n\\n{executive_summary}\\n\\nNegative weeks: {negative_week_details}\\nRisks: {risk_factors}\\nMitigation: {mitigation_actions}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 612. COMMISSION CALCULATOR BOT (8 nodes)
  // ===================================================================

  "Commission Calculator Bot": {
    summary:
      "Automated monthly commission calculation with quota attainment, tier multipliers, and finance approval. A monthly payout trigger pulls closed-won deals from Salesforce, loads plan versions and incentives, runCode performs deal-by-deal calculation with quota attainment and tier multipliers, generates commission statements, routes through a finance approval gate, and notifies reps via email.",
    nodes: [
      trigger("Schedule: Monthly commission payout cycle", "schedule", {
        schedule: "0 6 1 * *",
        howItStarts:
          "Runs on the 1st of each month to calculate commissions for all reps based on the prior month's closed deals",
        setup:
          "Configure Salesforce API for closed-won deals. Set commission plan tiers and quotas in the runCode node. Connect finance approval routing.",
        inputFields: [],
      }),

      // 2. Closed-won deals pull (Salesforce)
      salesforceNode(
        "2",
        "Pull closed-won deals from Salesforce",
        280,
        "GET",
        "query?q=SELECT+Id,Name,Amount,CloseDate,OwnerId,Owner.Name,Owner.Email,StageName+FROM+Opportunity+WHERE+StageName='Closed Won'+AND+CloseDate=LAST_MONTH",
        [],
        [
          { name: "records", customName: "closed_deals", type: "string" },
          { name: "totalSize", customName: "deal_count", type: "number" },
        ],
      ),

      // 3. Plan version + incentive load
      n("3", "hitApi", "Load commission plan & incentives", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{commission_sheet_id}/values/CommissionPlans!A1:Z50",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "commission_plans", type: "string" },
          ],
        },
      }),

      // 4. Deal-by-deal calculation (runCode)
      n("4", "runCode", "Calculate commissions per rep", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate commissions per rep with quota attainment and tier multipliers
const deals = JSON.parse(vars.closed_deals || "[]");
const repSummary = {};

deals.forEach(deal => {
  const repName = deal.Owner?.Name || "Unknown";
  const repEmail = deal.Owner?.Email || "";
  const amount = parseFloat(deal.Amount || "0");

  if (!repSummary[repName]) {
    repSummary[repName] = { email: repEmail, totalRevenue: 0, dealCount: 0, commission: 0 };
  }
  repSummary[repName].totalRevenue += amount;
  repSummary[repName].dealCount++;
});

// Apply commission tiers
const quota = 100000; // monthly quota
let totalPayout = 0;
const statements = [];

Object.entries(repSummary).forEach(([rep, data]) => {
  const attainment = data.totalRevenue / quota;
  let rate = 0.08; // base 8%
  if (attainment >= 1.5) rate = 0.14; // accelerator
  else if (attainment >= 1.0) rate = 0.11; // target tier
  else if (attainment >= 0.75) rate = 0.08; // base
  else rate = 0.05; // below threshold

  const commission = data.totalRevenue * rate;
  data.commission = commission;
  totalPayout += commission;
  statements.push(rep + ": $" + commission.toFixed(2) + " (" + (attainment * 100).toFixed(0) + "% attainment, " + data.dealCount + " deals, " + (rate * 100) + "% rate)");
});

return {
  total_payout: totalPayout.toFixed(2),
  rep_count: Object.keys(repSummary).length,
  deal_count: parseInt(vars.deal_count || "0", 10),
  commission_statements: statements.join("; "),
  rep_data: JSON.stringify(repSummary),
  top_earner: statements[0] || "None",
  payout_summary: Object.keys(repSummary).length + " reps | " + vars.deal_count + " deals | $" + totalPayout.toFixed(0) + " total payout"
};`,
          outputVariables: [
            { name: "total_payout", type: "string" },
            { name: "rep_count", type: "number" },
            { name: "deal_count", type: "number" },
            { name: "commission_statements", type: "string" },
            { name: "rep_data", type: "string" },
            { name: "top_earner", type: "string" },
            { name: "payout_summary", type: "string" },
          ],
        },
      }),

      // 5. Quota attainment + tier multipliers — statement generation
      aiNode(
        "5",
        "AI generate commission narratives",
        1000,
        "You are a sales compensation analyst. Create a professional commission summary for each rep and an overall payout summary for finance review. Include quota attainment percentages and tier details. Respond JSON: {finance_summary: string, rep_narratives: string, anomalies: string, month_comparison: string}",
        "Commission statements: {commission_statements} | Total payout: ${total_payout} | Reps: {rep_count} | Deals: {deal_count} | Top earner: {top_earner}",
        [
          { field: "finance_summary", as: "finance_summary", type: "string" },
          { field: "rep_narratives", as: "rep_narratives", type: "string" },
          { field: "anomalies", as: "anomalies", type: "string" },
          { field: "month_comparison", as: "month_comparison", type: "string" },
        ],
      ),

      // 6. Statement generation (Sheets)
      sheetsNode(
        "6",
        "Create commission statements (Sheets)",
        1240,
        "CommissionStatements",
        '[["{payout_summary}","{total_payout}","{rep_count}","{deal_count}","{commission_statements}","{anomalies}","{month_comparison}"]]',
      ),

      // 7. Finance approval gate
      n("7", "approvalGate", "Finance: Approve commission payout", 1480, {
        approvalGateData: {
          message:
            "Monthly Commission Payout Approval\\n\\n{payout_summary}\\n\\nTop earner: {top_earner}\\nAnomalies: {anomalies}\\n\\nFinance summary: {finance_summary}\\n\\nApprove to process payout of ${total_payout}.",
        },
      }),

      // 8. Rep notification (email)
      emailNode(
        "8",
        "Email commission statements to reps",
        1720,
        "{sales_team_email}",
        "Your Monthly Commission Statement — ${total_payout} total payout",
        "Monthly Commission Summary:\\n\\n{rep_narratives}\\n\\nTotal payout: ${total_payout}\\nDeals closed: {deal_count}\\n\\n{month_comparison}\\n\\nIf you have questions about your commission, please contact the sales compensation team.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 613. COMMISSION DISPUTE RESOLUTION BOT (8 nodes)
  // ===================================================================

  "Commission Dispute Resolution Bot": {
    summary:
      "Resolves sales commission disputes with transparent recalculation and committee-based approval. A rep dispute trigger pulls deal records and plan details, runCode performs a recalculation comparison, compiles evidence, AI recommends a resolution, routes to a committee approval gate, processes any adjustments, and notifies the rep via email.",
    nodes: [
      trigger("Webhook: Commission dispute filed", "webhook_app", {
        source: "Sales Portal / HR System",
        event: "commission.dispute_filed",
        howItStarts:
          "A sales rep files a commission dispute, triggering the investigation and resolution workflow",
        setup:
          "Connect your sales portal or HR system to POST dispute details (rep ID, period, disputed deals, claimed amount) to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract dispute + deal records pull
      aiNode(
        "2",
        "AI extract dispute details",
        280,
        "You are a compensation dispute specialist. Parse this dispute submission regardless of format. Extract the rep details, disputed period, specific deals in question, claimed commission amount, current paid amount, and the rep's rationale. Respond JSON: {rep_name: string, rep_email: string, rep_id: string, dispute_period: string, disputed_deals: string, claimed_amount: number, paid_amount: number, dispute_rationale: string, deal_ids: string}",
        "{webhook_payload}",
        [
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "rep_email", as: "rep_email", type: "string" },
          { field: "rep_id", as: "rep_id", type: "string" },
          { field: "dispute_period", as: "dispute_period", type: "string" },
          { field: "disputed_deals", as: "disputed_deals", type: "string" },
          { field: "claimed_amount", as: "claimed_amount", type: "number" },
          { field: "paid_amount", as: "paid_amount", type: "number" },
          {
            field: "dispute_rationale",
            as: "dispute_rationale",
            type: "string",
          },
          { field: "deal_ids", as: "deal_ids", type: "string" },
        ],
      ),

      // 3. Deal records + plan pull
      salesforceNode(
        "3",
        "Pull disputed deal records",
        520,
        "GET",
        "query?q=SELECT+Id,Name,Amount,CloseDate,OwnerId,StageName,Commission_Rate__c+FROM+Opportunity+WHERE+Id+IN+({deal_ids})",
        [],
        [
          { name: "records", customName: "deal_records", type: "string" },
          { name: "totalSize", customName: "record_count", type: "number" },
        ],
      ),

      // 4. Recalculation comparison (runCode)
      n("4", "runCode", "Recalculate & compare commissions", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Recalculate commissions for disputed deals and compare with paid amounts
const deals = JSON.parse(vars.deal_records || "[]");
const claimedAmount = parseFloat(vars.claimed_amount || "0");
const paidAmount = parseFloat(vars.paid_amount || "0");

let recalcTotal = 0;
const dealDetails = [];

deals.forEach(deal => {
  const amount = parseFloat(deal.Amount || "0");
  const rate = parseFloat(deal.Commission_Rate__c || "0.08");
  const commission = amount * rate;
  recalcTotal += commission;
  dealDetails.push(deal.Name + ": $" + amount + " x " + (rate * 100).toFixed(1) + "% = $" + commission.toFixed(2));
});

const originalDiff = claimedAmount - paidAmount;
const recalcDiff = recalcTotal - paidAmount;
const repClaimValid = Math.abs(claimedAmount - recalcTotal) < 100; // within $100

return {
  recalculated_total: recalcTotal.toFixed(2),
  difference_from_paid: recalcDiff.toFixed(2),
  rep_claim_matches: repClaimValid ? "yes" : "no",
  adjustment_needed: recalcDiff > 0 ? "yes" : "no",
  adjustment_amount: Math.max(0, recalcDiff).toFixed(2),
  deal_breakdown: dealDetails.join("; "),
  evidence_summary: "Paid: $" + paidAmount.toFixed(2) + " | Recalculated: $" + recalcTotal.toFixed(2) + " | Claimed: $" + claimedAmount.toFixed(2) + " | Diff: $" + recalcDiff.toFixed(2)
};`,
          outputVariables: [
            { name: "recalculated_total", type: "string" },
            { name: "difference_from_paid", type: "string" },
            { name: "rep_claim_matches", type: "string" },
            { name: "adjustment_needed", type: "string" },
            { name: "adjustment_amount", type: "string" },
            { name: "deal_breakdown", type: "string" },
            { name: "evidence_summary", type: "string" },
          ],
        },
      }),

      // 5. AI resolution recommendation
      aiNode(
        "5",
        "AI recommend resolution",
        1000,
        "You are a sales compensation committee advisor. Analyze the dispute evidence, recalculation results, and rep's rationale. Provide a fair, evidence-based recommendation. Respond JSON: {recommendation: string, resolution_type: string, suggested_adjustment: number, reasoning: string, precedent_notes: string}",
        "Rep: {rep_name} | Period: {dispute_period} | Rationale: {dispute_rationale} | Evidence: {evidence_summary} | Deal breakdown: {deal_breakdown} | Claim matches recalc: {rep_claim_matches} | Adjustment needed: {adjustment_needed}",
        [
          { field: "recommendation", as: "recommendation", type: "string" },
          { field: "resolution_type", as: "resolution_type", type: "string" },
          {
            field: "suggested_adjustment",
            as: "suggested_adjustment",
            type: "number",
          },
          { field: "reasoning", as: "resolution_reasoning", type: "string" },
          { field: "precedent_notes", as: "precedent_notes", type: "string" },
        ],
      ),

      // 6. Committee routing (approval gate)
      n("6", "approvalGate", "Compensation committee review", 1240, {
        approvalGateData: {
          message:
            "Commission Dispute Review\\n\\nRep: {rep_name} | Period: {dispute_period}\\nPaid: ${paid_amount} | Claimed: ${claimed_amount} | Recalculated: ${recalculated_total}\\n\\nEvidence: {evidence_summary}\\nDeal breakdown: {deal_breakdown}\\n\\nRecommendation: {recommendation}\\nResolution type: {resolution_type}\\nSuggested adjustment: ${suggested_adjustment}\\nReasoning: {resolution_reasoning}\\n\\nApprove to process the recommended resolution.",
        },
      }),

      // 7. Adjustment processing (Sheets)
      sheetsNode(
        "7",
        "Process commission adjustment",
        1480,
        "CommissionAdjustments",
        '[["{rep_name}","{rep_id}","{dispute_period}","{paid_amount}","{claimed_amount}","{recalculated_total}","{suggested_adjustment}","{resolution_type}","{recommendation}","{resolution_reasoning}"]]',
      ),

      // 8. Rep notification (email)
      emailNode(
        "8",
        "Notify rep of dispute resolution",
        1720,
        "{rep_email}",
        "Commission dispute resolved — {resolution_type}",
        "Hi {rep_name},\\n\\nYour commission dispute for {dispute_period} has been reviewed and resolved.\\n\\nOriginal paid: ${paid_amount}\\nYour claim: ${claimed_amount}\\nRecalculated: ${recalculated_total}\\n\\nResolution: {resolution_type}\\nAdjustment: ${suggested_adjustment}\\n\\nExplanation: {resolution_reasoning}\\n\\nDeal breakdown:\\n{deal_breakdown}\\n\\nIf you have further questions, please contact the compensation team.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 614. CONTRACT FINANCIAL SUMMARY EXTRACTOR (7 nodes)
  // ===================================================================

  "Contract Financial Summary Extractor": {
    summary:
      "Extracts financial terms from uploaded contracts with revenue recognition classification and risk flagging. A contract upload trigger runs AI extraction of financial terms, runCode classifies revenue recognition treatment, updates the forecast in Sheets, checks for risky clauses via condition, notifies finance and legal teams, and logs the contract summary.",
    nodes: [
      trigger("Webhook: Contract uploaded for review", "webhook_app", {
        source: "Document Management / CLM System",
        event: "contract.uploaded",
        howItStarts:
          "A new contract is uploaded to the document management system, triggering financial term extraction and classification",
        setup:
          "Connect your CLM or document management system to POST contract text or PDF data to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI financial term extraction
      aiNode(
        "2",
        "AI extract financial terms from contract",
        280,
        "You are a contract financial analyst. Extract all financial terms from this contract regardless of format. Find total contract value, payment schedule, billing frequency, term length, auto-renewal clauses, termination fees, SLA penalties, discount terms, and any variable pricing. Respond JSON: {contract_value: number, payment_schedule: string, billing_frequency: string, term_months: number, auto_renewal: string, termination_fee: number, sla_penalties: string, discount_terms: string, variable_pricing: string, currency: string, customer_name: string, contract_id: string, has_risky_clauses: string, risky_clause_details: string}",
        "{webhook_payload}",
        [
          { field: "contract_value", as: "contract_value", type: "number" },
          { field: "payment_schedule", as: "payment_schedule", type: "string" },
          {
            field: "billing_frequency",
            as: "billing_frequency",
            type: "string",
          },
          { field: "term_months", as: "term_months", type: "number" },
          { field: "auto_renewal", as: "auto_renewal", type: "string" },
          { field: "termination_fee", as: "termination_fee", type: "number" },
          { field: "sla_penalties", as: "sla_penalties", type: "string" },
          { field: "discount_terms", as: "discount_terms", type: "string" },
          { field: "variable_pricing", as: "variable_pricing", type: "string" },
          { field: "currency", as: "currency", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "contract_id", as: "contract_id", type: "string" },
          {
            field: "has_risky_clauses",
            as: "has_risky_clauses",
            type: "string",
          },
          {
            field: "risky_clause_details",
            as: "risky_clause_details",
            type: "string",
          },
        ],
      ),

      // 3. Revenue recognition classification (runCode)
      n("3", "runCode", "Classify revenue recognition treatment", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify revenue recognition treatment per ASC 606
const value = parseFloat(vars.contract_value || "0");
const termMonths = parseInt(vars.term_months || "12", 10);
const frequency = vars.billing_frequency || "monthly";
const variablePricing = vars.variable_pricing || "none";
const hasPerformanceObligation = variablePricing !== "none";

// Monthly recognized revenue
const monthlyRevRec = value / termMonths;
const annualRevRec = monthlyRevRec * 12;

// Classification
let revRecType = "straight_line";
if (hasPerformanceObligation) revRecType = "milestone_based";
if (frequency === "upfront" || frequency === "annual_prepay") revRecType = "deferred_ratably";

const complexRevRec = revRecType !== "straight_line" || variablePricing !== "none";

return {
  rev_rec_type: revRecType,
  monthly_rev_rec: monthlyRevRec.toFixed(2),
  annual_rev_rec: annualRevRec.toFixed(2),
  is_complex: complexRevRec ? "yes" : "no",
  rev_rec_summary: revRecType + ": $" + monthlyRevRec.toFixed(0) + "/mo ($" + annualRevRec.toFixed(0) + "/yr) over " + termMonths + " months",
  forecast_impact: "Total: $" + value + " | Annual impact: $" + annualRevRec.toFixed(0)
};`,
          outputVariables: [
            { name: "rev_rec_type", type: "string" },
            { name: "monthly_rev_rec", type: "string" },
            { name: "annual_rev_rec", type: "string" },
            { name: "is_complex", type: "string" },
            { name: "rev_rec_summary", type: "string" },
            { name: "forecast_impact", type: "string" },
          ],
        },
      }),

      // 4. Forecast update (Sheets)
      sheetsNode(
        "4",
        "Update revenue forecast (Sheets)",
        760,
        "RevenueForecast",
        '[["{contract_id}","{customer_name}","{contract_value}","{term_months}","{rev_rec_type}","{monthly_rev_rec}","{annual_rev_rec}","{billing_frequency}","{payment_schedule}"]]',
      ),

      // 5. Risky clause condition
      n("5", "condition", "Contract has risky financial clauses?", 1000, {
        conditionData: [
          {
            variable: "has_risky_clauses",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Finance/legal notification (True path = risky clauses found)
      emailNode(
        "6",
        "Alert finance & legal on risky clauses",
        1240,
        "{finance_legal_email}",
        "⚠️ Risky clauses in contract {contract_id} — {customer_name}",
        "Contract Financial Summary Alert:\\n\\nContract: {contract_id} | Customer: {customer_name}\\nValue: {currency} {contract_value} | Term: {term_months} months\\n\\nRev Rec: {rev_rec_summary}\\nComplex treatment: {is_complex}\\n\\nRISKY CLAUSES IDENTIFIED:\\n{risky_clause_details}\\n\\nSLA penalties: {sla_penalties}\\nTermination fee: {currency} {termination_fee}\\nVariable pricing: {variable_pricing}\\nDiscount terms: {discount_terms}\\n\\nPlease review before execution.",
      ),

      // 7. Contract summary logging
      sheetsNode(
        "7",
        "Log contract financial summary",
        1480,
        "ContractSummary",
        '[["{contract_id}","{customer_name}","{contract_value}","{currency}","{term_months}","{rev_rec_type}","{monthly_rev_rec}","{has_risky_clauses}","{risky_clause_details}","{auto_renewal}","{termination_fee}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // Risky → alert
      e("5", "7", "output_2"), // Not risky → log directly
      e("6", "7"), // After alert → log
    ],
  },

  // ===================================================================
  // 615. CREDIT MEMO PROCESSOR (8 nodes)
  // ===================================================================

  "Credit Memo Processor": {
    summary:
      "Processes credit memo requests with validation, duplicate detection, amount-based routing, and ledger updates. A credit request trigger validates the invoice and reason via runCode, checks for duplicates, routes small credits for auto-approval and large credits through an approval gate, issues the credit via Stripe, updates the ledger in Sheets, notifies the customer, and logs the approval trail.",
    nodes: [
      trigger("Webhook: Credit memo request submitted", "webhook_app", {
        source: "Support / Finance Portal",
        event: "credit.request_submitted",
        howItStarts:
          "A credit memo request is submitted through the support portal or finance system, triggering validation and processing",
        setup:
          "Connect your support system or finance portal to POST credit requests (customer, invoice, amount, reason) to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract request + invoice/reason validation (runCode)
      aiNode(
        "2",
        "AI extract credit memo request",
        280,
        "You are an AR specialist. Parse this credit memo request regardless of format. Extract customer details, invoice reference, credit amount, reason category, and supporting details. Respond JSON: {customer_email: string, customer_name: string, customer_id: string, invoice_number: string, credit_amount: number, reason: string, reason_detail: string, requestor: string, currency: string}",
        "{webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "credit_amount", as: "credit_amount", type: "number" },
          { field: "reason", as: "credit_reason", type: "string" },
          { field: "reason_detail", as: "reason_detail", type: "string" },
          { field: "requestor", as: "requestor", type: "string" },
          { field: "currency", as: "currency", type: "string" },
        ],
      ),

      // 3. Validation (runCode)
      n("3", "runCode", "Validate request & check thresholds", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate credit memo request and determine approval path
const amount = parseFloat(vars.credit_amount || "0");
const invoiceNum = vars.invoice_number || "";
const reason = vars.credit_reason || "";
const autoApprovalMax = 500;
const memoId = "CM-" + Date.now().toString(36).toUpperCase();

const valid = amount > 0 && invoiceNum.length > 0 && reason.length > 0;
const isSmall = amount <= autoApprovalMax;
const isDuplicate = false; // would check Sheets in production

return {
  memo_id: memoId,
  validation_passed: valid ? "yes" : "no",
  is_duplicate: isDuplicate ? "yes" : "no",
  is_small_credit: isSmall ? "yes" : "no",
  approval_path: isSmall ? "auto" : "manual",
  validation_notes: valid ? "Valid request: $" + amount + " for invoice " + invoiceNum : "Validation failed — missing required fields",
  threshold_info: isSmall ? "Under $" + autoApprovalMax + " — auto-approval eligible" : "Over $" + autoApprovalMax + " — requires manual approval"
};`,
          outputVariables: [
            { name: "memo_id", type: "string" },
            { name: "validation_passed", type: "string" },
            { name: "is_duplicate", type: "string" },
            { name: "is_small_credit", type: "string" },
            { name: "approval_path", type: "string" },
            { name: "validation_notes", type: "string" },
            { name: "threshold_info", type: "string" },
          ],
        },
      }),

      // 4. Duplicate check condition
      n("4", "condition", "Is duplicate or invalid request?", 760, {
        conditionData: [
          {
            variable: "is_duplicate",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "validation_passed",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Small auto-approval (True path — valid + not duplicate → check amount)
      n("5", "condition", "Small credit — auto-approve?", 1000, {
        conditionData: [
          {
            variable: "is_small_credit",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5b. Large approval gate (False path — needs manual approval)
      n(
        "5b",
        "approvalGate",
        "Finance: Approve large credit memo",
        1000,
        {
          approvalGateData: {
            message:
              "Credit Memo Approval Required\\n\\nMemo: {memo_id}\\nCustomer: {customer_name} ({customer_email})\\nInvoice: {invoice_number}\\nAmount: {currency} {credit_amount}\\nReason: {credit_reason} — {reason_detail}\\nRequestor: {requestor}\\n\\n{threshold_info}\\n\\nApprove to issue credit.",
          },
        },
        400,
      ),

      // 6. Credit issuance (Stripe)
      stripeNode(
        "6",
        "Issue credit via Stripe",
        1240,
        "POST",
        "credit_notes",
        [
          { key: "invoice", value: "{invoice_number}" },
          { key: "amount", value: "{credit_amount}" },
          { key: "reason", value: "order_change" },
          { key: "memo", value: "{memo_id}: {credit_reason}" },
        ],
        [
          { name: "id", customName: "credit_note_id", type: "string" },
          { name: "status", customName: "credit_status", type: "string" },
          { name: "number", customName: "credit_note_number", type: "string" },
        ],
      ),

      // 7. Ledger update + customer notification
      sheetsNode(
        "7",
        "Update AR ledger (Sheets)",
        1480,
        "ARLedger",
        '[["{memo_id}","{credit_note_id}","{customer_name}","{invoice_number}","{credit_amount}","{credit_reason}","{approval_path}","{credit_status}","{requestor}"]]',
      ),

      // 8. Customer notification (email) + approval trail
      emailNode(
        "8",
        "Notify customer of credit issued",
        1720,
        "{customer_email}",
        "Credit memo {memo_id} issued — {currency} {credit_amount}",
        "Dear {customer_name},\\n\\nA credit memo has been applied to your account.\\n\\nCredit memo: {memo_id}\\nCredit note: {credit_note_number}\\nOriginal invoice: {invoice_number}\\nCredit amount: {currency} {credit_amount}\\nReason: {credit_reason}\\n\\nThis credit will be applied to your next invoice or can be used as account credit.\\n\\nIf you have any questions, please reply to this email.\\n\\nBest regards,\\nAccounts Receivable",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Valid + not duplicate → check amount
      e("4", "7", "output_2"), // Invalid/duplicate → log only (skip issuance)
      e("5", "6", "output_1"), // Small → auto-approve → issue credit
      e("5", "5b", "output_2"), // Large → approval gate
      e("5b", "6"), // After approval → issue credit
      e("6", "7"), // Credit issued → ledger update
      e("7", "8"), // Ledger → customer notification
    ],
  },

  // ===================================================================
  // 616. CRYPTO PRICE ALERT (7 nodes)
  // ===================================================================

  "Crypto Price Alert": {
    summary:
      "Real-time cryptocurrency price monitoring with AI market context and multi-channel alerts. A frequent schedule fetches crypto prices, runCode compares against thresholds, routes through a breach condition, AI provides market context, sends Slack and SMS alerts, and logs alert performance to Sheets.",
    nodes: [
      trigger("Schedule: Frequent crypto price check", "schedule", {
        schedule: "*/5 * * * *",
        howItStarts:
          "Runs every 5 minutes to check cryptocurrency prices against configured thresholds and alert on significant movements",
        setup:
          "Configure the crypto pairs and alert thresholds in the runCode node. Connect Slack webhook and Twilio for SMS alerts.",
        inputFields: [],
      }),

      // 2. Crypto price fetch
      n("2", "hitApi", "Fetch crypto prices (CoinGecko)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,solana&vs_currencies=usd&include_24hr_change=true&include_24hr_vol=true",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "bitcoin.usd", customName: "btc_price", type: "number" },
            {
              name: "bitcoin.usd_24h_change",
              customName: "btc_24h_change",
              type: "number",
            },
            { name: "ethereum.usd", customName: "eth_price", type: "number" },
            {
              name: "ethereum.usd_24h_change",
              customName: "eth_24h_change",
              type: "number",
            },
            { name: "solana.usd", customName: "sol_price", type: "number" },
            {
              name: "solana.usd_24h_change",
              customName: "sol_24h_change",
              type: "number",
            },
          ],
        },
      }),

      // 3. Threshold comparison (runCode)
      n("3", "runCode", "Compare prices to alert thresholds", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compare current prices to configured thresholds
const btcPrice = parseFloat(vars.btc_price || "0");
const ethPrice = parseFloat(vars.eth_price || "0");
const solPrice = parseFloat(vars.sol_price || "0");
const btcChange = parseFloat(vars.btc_24h_change || "0");
const ethChange = parseFloat(vars.eth_24h_change || "0");
const solChange = parseFloat(vars.sol_24h_change || "0");

// Configurable thresholds
const changeThreshold = 5; // alert on 5%+ 24h change
const alerts = [];

if (Math.abs(btcChange) >= changeThreshold) alerts.push("BTC: $" + btcPrice.toFixed(0) + " (" + (btcChange > 0 ? "+" : "") + btcChange.toFixed(2) + "%)");
if (Math.abs(ethChange) >= changeThreshold) alerts.push("ETH: $" + ethPrice.toFixed(0) + " (" + (ethChange > 0 ? "+" : "") + ethChange.toFixed(2) + "%)");
if (Math.abs(solChange) >= changeThreshold) alerts.push("SOL: $" + solPrice.toFixed(2) + " (" + (solChange > 0 ? "+" : "") + solChange.toFixed(2) + "%)");

const hasAlerts = alerts.length > 0 ? "yes" : "no";
const maxChange = Math.max(Math.abs(btcChange), Math.abs(ethChange), Math.abs(solChange));

return {
  threshold_breached: hasAlerts,
  alert_count: alerts.length,
  alert_details: alerts.join("; ") || "No threshold breaches",
  max_change_pct: maxChange.toFixed(2),
  price_summary: "BTC: $" + btcPrice.toFixed(0) + " (" + btcChange.toFixed(1) + "%) | ETH: $" + ethPrice.toFixed(0) + " (" + ethChange.toFixed(1) + "%) | SOL: $" + solPrice.toFixed(2) + " (" + solChange.toFixed(1) + "%)"
};`,
          outputVariables: [
            { name: "threshold_breached", type: "string" },
            { name: "alert_count", type: "number" },
            { name: "alert_details", type: "string" },
            { name: "max_change_pct", type: "string" },
            { name: "price_summary", type: "string" },
          ],
        },
      }),

      // 4. Breach condition
      n("4", "condition", "Price threshold breached?", 760, {
        conditionData: [
          {
            variable: "threshold_breached",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. AI market context (True path)
      aiNode(
        "5",
        "AI provide market context",
        1000,
        "You are a crypto market analyst. Given current prices and significant price movements, provide brief market context explaining likely reasons for the movement and short-term outlook. Respond JSON: {market_context: string, likely_cause: string, outlook: string, risk_level: string}",
        "Price summary: {price_summary} | Alerts: {alert_details} | Max change: {max_change_pct}%",
        [
          { field: "market_context", as: "market_context", type: "string" },
          { field: "likely_cause", as: "likely_cause", type: "string" },
          { field: "outlook", as: "outlook", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
        ],
      ),

      // 6. Slack + SMS alert
      slackNode(
        "6",
        "Slack + SMS crypto price alert",
        1240,
        "🪙 *Crypto Price Alert*\\n{alert_details}\\n\\n📊 {price_summary}\\n\\n*Context:* {market_context}\\n*Likely cause:* {likely_cause}\\n*Outlook:* {outlook}\\n*Risk:* {risk_level}",
      ),

      // 7. Alert performance logging (Sheets)
      sheetsNode(
        "7",
        "Log alert performance (Sheets)",
        1480,
        "CryptoAlerts",
        '[["{price_summary}","{threshold_breached}","{alert_count}","{alert_details}","{max_change_pct}","{market_context}","{risk_level}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Breach → AI context
      e("4", "7", "output_2"), // No breach → log only
      e("5", "6"), // Context → alerts
      e("6", "7"), // Alerts → log
    ],
  },

  // ===================================================================
  // 617. DAILY EXPENSE LOGGER (7 nodes)
  // ===================================================================

  "Daily Expense Logger": {
    summary:
      "Automated expense logging from receipt emails with AI extraction, deduplication, and accounting sync. A receipt email trigger runs AI extraction of vendor, amount, and category, runCode classifies the expense, checks for duplicates via condition, appends to the expense tracker in Sheets, syncs with accounting, and confirms to the submitter.",
    nodes: [
      trigger("Email: Receipt forwarded for expense logging", "email", {
        howItStarts:
          "An employee forwards a receipt email to the expense logging address, triggering automated extraction and categorization",
        setup:
          "Set up a dedicated email address (expenses@company.com) to receive forwarded receipts. Connect Google Sheets for expense tracking.",
        inputFields: [],
      }),

      // 2. AI extraction (vendor/amount/category)
      aiNode(
        "2",
        "AI extract receipt details",
        280,
        "You are an expense processing specialist. Extract all details from this forwarded receipt email regardless of format. Find the vendor name, transaction amount, currency, date, category (meals, travel, software, supplies, etc.), payment method, and submitter email. Look for totals, tax, and tips. Respond JSON: {vendor_name: string, amount: number, currency: string, transaction_date: string, category: string, payment_method: string, submitter_email: string, tax_amount: number, tip_amount: number, description: string, receipt_id: string}",
        "{webhook_payload}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "amount", as: "expense_amount", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "transaction_date", as: "transaction_date", type: "string" },
          { field: "category", as: "expense_category", type: "string" },
          { field: "payment_method", as: "payment_method", type: "string" },
          { field: "submitter_email", as: "submitter_email", type: "string" },
          { field: "tax_amount", as: "tax_amount", type: "number" },
          { field: "tip_amount", as: "tip_amount", type: "number" },
          { field: "description", as: "expense_description", type: "string" },
          { field: "receipt_id", as: "receipt_id", type: "string" },
        ],
      ),

      // 3. Classification (runCode)
      n("3", "runCode", "Classify & generate GL code", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map expense category to GL code and validate
const category = (vars.expense_category || "").toLowerCase();
const amount = parseFloat(vars.expense_amount || "0");
const expenseId = "EXP-" + Date.now().toString(36).toUpperCase();

const glMapping = {
  meals: "6200-MEALS",
  travel: "6300-TRAVEL",
  software: "6400-SOFTWARE",
  supplies: "6100-SUPPLIES",
  entertainment: "6250-ENTERTAIN",
  transportation: "6310-TRANSPORT",
  lodging: "6320-LODGING",
  telecom: "6410-TELECOM",
  training: "6500-TRAINING",
  other: "6900-OTHER"
};

const glCode = glMapping[category] || glMapping.other;
const needsReceipt = amount > 25;
const isDuplicate = false; // would check Sheets in production

return {
  expense_id: expenseId,
  gl_code: glCode,
  is_duplicate: isDuplicate ? "yes" : "no",
  needs_receipt: needsReceipt ? "yes" : "no",
  total_with_tax: (amount + parseFloat(vars.tax_amount || "0")).toFixed(2),
  expense_summary: expenseId + ": " + vars.vendor_name + " $" + amount.toFixed(2) + " [" + glCode + "]"
};`,
          outputVariables: [
            { name: "expense_id", type: "string" },
            { name: "gl_code", type: "string" },
            { name: "is_duplicate", type: "string" },
            { name: "needs_receipt", type: "string" },
            { name: "total_with_tax", type: "string" },
            { name: "expense_summary", type: "string" },
          ],
        },
      }),

      // 4. Duplicate check condition
      n("4", "condition", "Is this a duplicate expense?", 760, {
        conditionData: [
          {
            variable: "is_duplicate",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Expense tracker append (Sheets) — True path (not duplicate)
      sheetsNode(
        "5",
        "Append to expense tracker (Sheets)",
        1000,
        "ExpenseTracker",
        '[["{expense_id}","{vendor_name}","{expense_amount}","{currency}","{transaction_date}","{expense_category}","{gl_code}","{payment_method}","{submitter_email}","{tax_amount}","{expense_description}"]]',
      ),

      // 6. Accounting sync
      n("6", "hitApi", "Sync expense to accounting system", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{accounting_api}/api/v1/expenses",
          headers: [
            { key: "Authorization", value: "Bearer {accounting_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "expense_id", value: "{expense_id}" },
            { key: "vendor", value: "{vendor_name}" },
            { key: "amount", value: "{expense_amount}" },
            { key: "gl_code", value: "{gl_code}" },
            { key: "date", value: "{transaction_date}" },
            { key: "description", value: "{expense_description}" },
          ],
          variables: [
            {
              name: "data.synced",
              customName: "accounting_synced",
              type: "boolean",
            },
          ],
        },
      }),

      // 7. Submitter confirmation (email)
      emailNode(
        "7",
        "Confirm expense logged to submitter",
        1480,
        "{submitter_email}",
        "Expense logged: {expense_id} — {vendor_name} {currency} {expense_amount}",
        "Your expense has been automatically logged:\\n\\nExpense ID: {expense_id}\\nVendor: {vendor_name}\\nAmount: {currency} {expense_amount} (tax: {currency} {tax_amount})\\nCategory: {expense_category}\\nGL Code: {gl_code}\\nDate: {transaction_date}\\nDescription: {expense_description}\\n\\nThis expense has been added to your expense tracker and synced to accounting.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Not duplicate → log
      e("4", "7", "output_2"), // Duplicate → notify only (skip logging)
      e("5", "6"), // Log → accounting sync
      e("6", "7"), // Sync → confirmation
    ],
  },

  // ===================================================================
  // 618. ERROR BUDGET TRACKER (8 nodes)
  // ===================================================================

  "Error Budget Tracker": {
    summary:
      "Tracks SLO error budgets with burn rate alerts and deployment freeze recommendations. A frequent schedule pulls uptime, error, and latency metrics, runCode computes error budget consumption, routes through a burn rate condition, sends engineering Slack alerts, recommends deployment freezes, updates the SLO dashboard in Sheets, and correlates with recent incidents.",
    nodes: [
      trigger("Schedule: Frequent error budget check", "schedule", {
        schedule: "*/15 * * * *",
        howItStarts:
          "Runs every 15 minutes to track SLO compliance, compute error budget burn rate, and alert when budgets are at risk",
        setup:
          "Configure your monitoring API (Datadog, New Relic, etc.) for metrics. Set SLO targets in the runCode node. Connect Slack for alerts.",
        inputFields: [],
      }),

      // 2. Uptime/error/latency metrics pull
      n("2", "hitApi", "Pull SLO metrics from monitoring", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{monitoring_api}/api/v1/slo/metrics?period=30d&include=uptime,error_rate,latency_p99",
          headers: [
            { key: "Authorization", value: "Bearer {monitoring_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.uptime_pct",
              customName: "uptime_pct",
              type: "number",
            },
            {
              name: "data.error_rate_pct",
              customName: "error_rate_pct",
              type: "number",
            },
            {
              name: "data.latency_p99_ms",
              customName: "latency_p99",
              type: "number",
            },
            {
              name: "data.total_requests",
              customName: "total_requests",
              type: "number",
            },
            {
              name: "data.error_count",
              customName: "error_count",
              type: "number",
            },
            {
              name: "data.period_hours",
              customName: "period_hours",
              type: "number",
            },
          ],
        },
      }),

      // 3. Error budget computation (runCode)
      n("3", "runCode", "Compute error budget & burn rate", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compute error budget consumption and burn rate
const uptimePct = parseFloat(vars.uptime_pct || "99.9");
const errorRate = parseFloat(vars.error_rate_pct || "0");
const latencyP99 = parseFloat(vars.latency_p99 || "0");
const totalRequests = parseInt(vars.total_requests || "0", 10);
const periodHours = parseFloat(vars.period_hours || "720"); // 30 days

// SLO targets
const uptimeSLO = 99.9;    // 99.9% uptime
const errorRateSLO = 0.1;  // 0.1% error rate max
const latencySLO = 500;    // 500ms p99

// Error budget = allowed downtime/errors in the period
const uptimeBudgetMinutes = (100 - uptimeSLO) / 100 * periodHours * 60; // ~43.2 min/month
const uptimeConsumedMinutes = (100 - uptimePct) / 100 * periodHours * 60;
const uptimeBudgetPct = uptimeBudgetMinutes > 0 ? (uptimeConsumedMinutes / uptimeBudgetMinutes * 100) : 0;

const errorBudgetTotal = errorRateSLO / 100 * totalRequests;
const errorBudgetConsumed = parseInt(vars.error_count || "0", 10);
const errorBudgetPct = errorBudgetTotal > 0 ? (errorBudgetConsumed / errorBudgetTotal * 100) : 0;

const latencyBreaching = latencyP99 > latencySLO;
const overallBudgetPct = Math.max(uptimeBudgetPct, errorBudgetPct);
const isCritical = overallBudgetPct >= 80;
const burnRatePerDay = overallBudgetPct / (periodHours / 24);
const daysUntilExhaustion = burnRatePerDay > 0 ? Math.floor((100 - overallBudgetPct) / burnRatePerDay) : 999;

return {
  burn_rate_critical: isCritical ? "yes" : "no",
  overall_budget_consumed_pct: overallBudgetPct.toFixed(1),
  uptime_budget_pct: uptimeBudgetPct.toFixed(1),
  error_budget_pct: errorBudgetPct.toFixed(1),
  latency_breaching: latencyBreaching ? "yes" : "no",
  burn_rate_per_day: burnRatePerDay.toFixed(2),
  days_until_exhaustion: daysUntilExhaustion,
  recommend_freeze: (isCritical || daysUntilExhaustion < 7) ? "yes" : "no",
  slo_summary: "Uptime: " + uptimePct.toFixed(3) + "% | Errors: " + errorRate.toFixed(3) + "% | P99: " + latencyP99 + "ms | Budget: " + overallBudgetPct.toFixed(1) + "% consumed"
};`,
          outputVariables: [
            { name: "burn_rate_critical", type: "string" },
            { name: "overall_budget_consumed_pct", type: "string" },
            { name: "uptime_budget_pct", type: "string" },
            { name: "error_budget_pct", type: "string" },
            { name: "latency_breaching", type: "string" },
            { name: "burn_rate_per_day", type: "string" },
            { name: "days_until_exhaustion", type: "number" },
            { name: "recommend_freeze", type: "string" },
            { name: "slo_summary", type: "string" },
          ],
        },
      }),

      // 4. Burn rate condition
      n("4", "condition", "Error budget burn rate critical?", 760, {
        conditionData: [
          {
            variable: "burn_rate_critical",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "recommend_freeze",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 5. Engineering Slack alert (True path)
      slackNode(
        "5",
        "Engineering: Error budget alert",
        1000,
        "🚨 *Error Budget Alert — {overall_budget_consumed_pct}% Consumed*\\n{slo_summary}\\n\\nUptime budget: {uptime_budget_pct}% used\\nError budget: {error_budget_pct}% used\\nLatency breaching SLO: {latency_breaching}\\nBurn rate: {burn_rate_per_day}%/day\\nDays until exhaustion: {days_until_exhaustion}\\n\\nDeployment freeze recommended: {recommend_freeze}",
      ),

      // 6. Deployment freeze recommendation
      slackNode(
        "6",
        "Recommend deployment freeze",
        1240,
        "🛑 *DEPLOYMENT FREEZE RECOMMENDED*\\nError budget at {overall_budget_consumed_pct}% — only {days_until_exhaustion} days until SLO breach.\\n\\nAll non-critical deployments should be paused until budget recovers.\\nFocus on reliability improvements and incident remediation.",
        400,
      ),

      // 7. SLO dashboard (Sheets)
      sheetsNode(
        "7",
        "Update SLO dashboard (Sheets)",
        1480,
        "SLODashboard",
        '[["{uptime_budget_pct}","{error_budget_pct}","{overall_budget_consumed_pct}","{burn_rate_per_day}","{days_until_exhaustion}","{latency_breaching}","{recommend_freeze}","{slo_summary}"]]',
      ),

      // 8. Incident correlation
      sheetsNode(
        "8",
        "Log for incident correlation",
        1720,
        "SLOTrend",
        '[["{overall_budget_consumed_pct}","{burn_rate_per_day}","{days_until_exhaustion}","{burn_rate_critical}","{recommend_freeze}","{slo_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Critical → engineering alert
      e("4", "7", "output_2"), // Not critical → dashboard update
      e("5", "6"), // Alert → freeze recommendation
      e("6", "7"), // Freeze → dashboard
      e("7", "8"), // Dashboard → trend log
    ],
  },

  // ===================================================================
  // 619. EXPENSE APPROVAL SLACK BOT (7 nodes)
  // ===================================================================

  "Expense Approval Slack Bot": {
    summary:
      "Slack-native expense submission and approval with AI receipt validation and policy compliance. A Slack submission trigger runs AI receipt validation, runCode checks policy compliance, routes by amount through a condition, presents a Slack approval card, queues for reimbursement with employee notification, and logs approvals to Sheets.",
    nodes: [
      trigger("Webhook: Expense submitted via Slack", "webhook_app", {
        source: "Slack",
        event: "expense.submitted",
        howItStarts:
          "An employee submits an expense via Slack (uploading a receipt photo or entering details), triggering validation and approval",
        setup:
          "Configure a Slack bot with /expense command or receipt upload detection. Route submissions to the workflow URL.",
        inputFields: [],
      }),

      // 2. Receipt validation (AI)
      aiNode(
        "2",
        "AI validate receipt & extract details",
        280,
        "You are an expense compliance specialist. Validate this expense submission from Slack. Extract vendor, amount, category, date, and check for receipt quality. Flag any suspicious items. Respond JSON: {vendor: string, amount: number, currency: string, category: string, expense_date: string, receipt_valid: string, suspicious_flags: string, employee_name: string, employee_email: string, department: string, description: string}",
        "{webhook_payload}",
        [
          { field: "vendor", as: "vendor", type: "string" },
          { field: "amount", as: "expense_amount", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "category", as: "expense_category", type: "string" },
          { field: "expense_date", as: "expense_date", type: "string" },
          { field: "receipt_valid", as: "receipt_valid", type: "string" },
          { field: "suspicious_flags", as: "suspicious_flags", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "description", as: "expense_description", type: "string" },
        ],
      ),

      // 3. Policy compliance check (runCode)
      n("3", "runCode", "Check policy compliance & routing", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check expense against company policy and determine approval routing
const amount = parseFloat(vars.expense_amount || "0");
const category = vars.expense_category || "other";
const receiptValid = vars.receipt_valid === "yes";
const expenseId = "EX-" + Date.now().toString(36).toUpperCase();

// Policy limits by category
const limits = { meals: 75, travel: 500, software: 200, supplies: 100, entertainment: 150, other: 100 };
const limit = limits[category.toLowerCase()] || limits.other;
const withinPolicy = amount <= limit && receiptValid;
const autoApproveMax = 50;
const needsManagerApproval = amount > autoApproveMax;

return {
  expense_id: expenseId,
  within_policy: withinPolicy ? "yes" : "no",
  needs_approval: needsManagerApproval ? "yes" : "no",
  category_limit: limit,
  over_limit: amount > limit ? "yes" : "no",
  policy_note: withinPolicy ? "Compliant: $" + amount + " within $" + limit + " " + category + " limit" : "Non-compliant: $" + amount + " exceeds $" + limit + " " + category + " limit",
  approval_summary: expenseId + ": " + vars.vendor + " $" + amount + " (" + category + ")"
};`,
          outputVariables: [
            { name: "expense_id", type: "string" },
            { name: "within_policy", type: "string" },
            { name: "needs_approval", type: "string" },
            { name: "category_limit", type: "number" },
            { name: "over_limit", type: "string" },
            { name: "policy_note", type: "string" },
            { name: "approval_summary", type: "string" },
          ],
        },
      }),

      // 4. Amount routing condition
      n("4", "condition", "Needs manager approval?", 760, {
        conditionData: [
          {
            variable: "needs_approval",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Slack approval card (True path)
      slackNode(
        "5",
        "Slack: Expense approval request",
        1000,
        "📝 *Expense Approval: {expense_id}*\\nEmployee: {employee_name} ({department})\\nVendor: {vendor} | Amount: {currency} {expense_amount}\\nCategory: {expense_category} | Date: {expense_date}\\nDescription: {expense_description}\\n\\n{policy_note}\\nReceipt valid: {receipt_valid}\\nFlags: {suspicious_flags}\\n\\nReact ✅ to approve or ❌ to reject.",
      ),

      // 6. Reimbursement queue + employee notification
      emailNode(
        "6",
        "Notify employee of expense status",
        1240,
        "{employee_email}",
        "Expense {expense_id} — {vendor} {currency} {expense_amount} — submitted",
        "Hi {employee_name},\\n\\nYour expense has been submitted and is being processed.\\n\\nExpense ID: {expense_id}\\nVendor: {vendor}\\nAmount: {currency} {expense_amount}\\nCategory: {expense_category}\\nDate: {expense_date}\\n\\nPolicy status: {policy_note}\\n\\nYou will be notified once approved.",
      ),

      // 7. Approval logging (Sheets)
      sheetsNode(
        "7",
        "Log expense approval (Sheets)",
        1480,
        "ExpenseApprovals",
        '[["{expense_id}","{employee_name}","{department}","{vendor}","{expense_amount}","{expense_category}","{within_policy}","{needs_approval}","{receipt_valid}","{suspicious_flags}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Needs approval → Slack card
      e("4", "6", "output_2"), // Auto-approved → notify employee
      e("5", "6"), // After approval → notify
      e("6", "7"), // Notify → log
    ],
  },

  // ===================================================================
  // 620. EXPENSE APPROVAL WORKFLOW (8 nodes)
  // ===================================================================

  "Expense Approval Workflow": {
    summary:
      "Multi-stage expense report approval with item validation, manager and finance review, and reimbursement queuing. A report submission trigger validates items via runCode, splits non-compliant items, routes through a manager approval gate, checks finance review conditions, queues for reimbursement, updates the status tracker in Sheets, and maintains an audit trail.",
    nodes: [
      trigger("Webhook: Expense report submitted", "webhook_app", {
        source: "Expense Management System",
        event: "report.submitted",
        howItStarts:
          "An employee submits a complete expense report, triggering multi-stage validation and approval",
        setup:
          "Connect your expense management system to POST report data (employee, line items, receipts, totals) to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI extract + item validation (runCode)
      aiNode(
        "2",
        "AI extract expense report details",
        280,
        "You are an expense report processor. Parse this expense report submission regardless of format. Extract employee details, all line items, total amount, and report metadata. Respond JSON: {employee_name: string, employee_email: string, employee_id: string, department: string, report_id: string, line_items: string, line_item_count: number, total_amount: number, currency: string, date_range: string, purpose: string}",
        "{webhook_payload}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "report_id", as: "report_id", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          { field: "line_item_count", as: "line_item_count", type: "number" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "date_range", as: "date_range", type: "string" },
          { field: "purpose", as: "purpose", type: "string" },
        ],
      ),

      // 3. Item validation (runCode)
      n("3", "runCode", "Validate line items & policy compliance", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate each line item against expense policy
const lineItems = JSON.parse(vars.line_items || "[]");
const total = parseFloat(vars.total_amount || "0");
let compliantCount = 0;
let nonCompliantCount = 0;
let nonCompliantItems = [];
const financeReviewThreshold = 5000;

lineItems.forEach((item, i) => {
  const amount = parseFloat(item.amount || "0");
  const hasReceipt = item.receipt === true || item.receipt === "yes";
  const needsReceipt = amount > 25;

  if (needsReceipt && !hasReceipt) {
    nonCompliantCount++;
    nonCompliantItems.push((item.description || "Item " + (i + 1)) + ": $" + amount + " (missing receipt)");
  } else {
    compliantCount++;
  }
});

const allCompliant = nonCompliantCount === 0;
const needsFinanceReview = total > financeReviewThreshold;

return {
  all_compliant: allCompliant ? "yes" : "no",
  compliant_count: compliantCount,
  non_compliant_count: nonCompliantCount,
  non_compliant_items: nonCompliantItems.join("; ") || "None",
  needs_finance_review: needsFinanceReview ? "yes" : "no",
  validation_summary: compliantCount + " compliant, " + nonCompliantCount + " non-compliant of " + vars.line_item_count + " items | Total: $" + total
};`,
          outputVariables: [
            { name: "all_compliant", type: "string" },
            { name: "compliant_count", type: "number" },
            { name: "non_compliant_count", type: "number" },
            { name: "non_compliant_items", type: "string" },
            { name: "needs_finance_review", type: "string" },
            { name: "validation_summary", type: "string" },
          ],
        },
      }),

      // 4. Non-compliant split — notify employee of issues
      emailNode(
        "4",
        "Notify employee of non-compliant items",
        760,
        "{employee_email}",
        "Expense report {report_id}: {non_compliant_count} item(s) need attention",
        "Hi {employee_name},\\n\\nYour expense report {report_id} has {non_compliant_count} item(s) that need correction:\\n\\n{non_compliant_items}\\n\\nPlease upload missing receipts or correct the flagged items. Compliant items ({compliant_count}) will proceed through approval.\\n\\nReport total: {currency} {total_amount}\\nDate range: {date_range}",
      ),

      // 5. Manager approval gate
      n("5", "approvalGate", "Manager: Review expense report", 1000, {
        approvalGateData: {
          message:
            "Expense Report Approval: {report_id}\\n\\nEmployee: {employee_name} ({department})\\nTotal: {currency} {total_amount} | Items: {line_item_count}\\nDate range: {date_range} | Purpose: {purpose}\\n\\nValidation: {validation_summary}\\nNon-compliant items: {non_compliant_items}\\n\\nApprove to proceed to reimbursement.",
        },
      }),

      // 6. Finance review condition
      n("6", "condition", "Requires finance review?", 1240, {
        conditionData: [
          {
            variable: "needs_finance_review",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Reimbursement queue + status tracker
      sheetsNode(
        "7",
        "Queue for reimbursement & update status",
        1480,
        "ReimbursementQueue",
        '[["{report_id}","{employee_name}","{employee_id}","{department}","{total_amount}","{currency}","{line_item_count}","{purpose}","{validation_summary}","approved"]]',
      ),

      // 8. Audit trail
      sheetsNode(
        "8",
        "Log approval audit trail",
        1720,
        "ExpenseAudit",
        '[["{report_id}","{employee_name}","{total_amount}","{all_compliant}","{non_compliant_count}","{needs_finance_review}","{validation_summary}","approved"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // Finance review needed → still queue (after review)
      e("6", "7", "output_2"), // No finance review → queue directly
      e("7", "8"), // Queue → audit trail
    ],
  },

  // ===================================================================
  // 621. EXPENSE REPORT HELPER (7 nodes)
  // ===================================================================

  "Expense Report Helper": {
    summary:
      "AI-powered expense report assistant that answers policy questions, assembles reports, and pre-scans for compliance. An employee help trigger runs AI policy Q&A, fetches pending expenses, assists with report assembly, runs a compliance pre-scan via runCode, submits the report, and sends a tracking confirmation via email.",
    nodes: [
      trigger("Webhook: Employee expense help request", "webhook_app", {
        source: "Slack / Internal Portal",
        event: "expense.help_requested",
        howItStarts:
          "An employee asks for help with expense reporting via Slack or the internal portal (policy questions, report assembly, or compliance checks)",
        setup:
          "Connect your Slack bot or internal portal to POST help requests to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI policy Q&A
      aiNode(
        "2",
        "AI answer expense policy question",
        280,
        "You are a friendly expense policy expert. Parse the employee's request and provide helpful guidance. If they need help with a specific policy question, answer it. If they want to assemble a report, help structure it. If they need a compliance check, note that you will scan their pending expenses. Respond JSON: {help_type: string, employee_name: string, employee_email: string, policy_answer: string, needs_report_assembly: string, needs_compliance_scan: string, employee_id: string}",
        "{webhook_payload}",
        [
          { field: "help_type", as: "help_type", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "policy_answer", as: "policy_answer", type: "string" },
          {
            field: "needs_report_assembly",
            as: "needs_report_assembly",
            type: "string",
          },
          {
            field: "needs_compliance_scan",
            as: "needs_compliance_scan",
            type: "string",
          },
          { field: "employee_id", as: "employee_id", type: "string" },
        ],
      ),

      // 3. Pending expense fetch
      n("3", "hitApi", "Fetch pending expenses for employee", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{expense_api}/api/v1/expenses?employee_id={employee_id}&status=pending&include=receipts",
          headers: [
            { key: "Authorization", value: "Bearer {expense_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data.expenses",
              customName: "pending_expenses",
              type: "string",
            },
            {
              name: "data.total_amount",
              customName: "pending_total",
              type: "number",
            },
            { name: "data.count", customName: "pending_count", type: "number" },
          ],
        },
      }),

      // 4. Report assembly assistance (AI)
      aiNode(
        "4",
        "AI assemble expense report",
        760,
        "You are an expense report assistant. Using the employee's pending expenses, assemble a complete expense report. Group items by category, verify totals, and flag any items missing receipts. Respond JSON: {report_draft: string, grouped_summary: string, total_verified: number, missing_receipts: string, missing_receipt_count: number, report_ready: string}",
        "Employee: {employee_name} | Pending expenses: {pending_expenses} | Total: ${pending_total} | Count: {pending_count} | Help type: {help_type}",
        [
          { field: "report_draft", as: "report_draft", type: "string" },
          { field: "grouped_summary", as: "grouped_summary", type: "string" },
          { field: "total_verified", as: "total_verified", type: "number" },
          { field: "missing_receipts", as: "missing_receipts", type: "string" },
          {
            field: "missing_receipt_count",
            as: "missing_receipt_count",
            type: "number",
          },
          { field: "report_ready", as: "report_ready", type: "string" },
        ],
      ),

      // 5. Compliance pre-scan (runCode)
      n("5", "runCode", "Pre-scan for compliance issues", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Pre-scan expense report for compliance issues before submission
const total = parseFloat(vars.total_verified || "0");
const missingReceipts = parseInt(vars.missing_receipt_count || "0", 10);
const pendingCount = parseInt(vars.pending_count || "0", 10);

const issues = [];
if (missingReceipts > 0) issues.push(missingReceipts + " item(s) missing receipts");
if (total > 10000) issues.push("Total exceeds $10K — requires VP approval");
if (pendingCount > 50) issues.push("High item count — consider splitting into multiple reports");

const compliancePass = issues.length === 0;
const reportId = "RPT-" + Date.now().toString(36).toUpperCase();

return {
  report_id: reportId,
  compliance_passed: compliancePass ? "yes" : "no",
  issue_count: issues.length,
  compliance_issues: issues.join("; ") || "None — report is compliant",
  submission_ready: (compliancePass || missingReceipts === 0) ? "yes" : "no"
};`,
          outputVariables: [
            { name: "report_id", type: "string" },
            { name: "compliance_passed", type: "string" },
            { name: "issue_count", type: "number" },
            { name: "compliance_issues", type: "string" },
            { name: "submission_ready", type: "string" },
          ],
        },
      }),

      // 6. Report submission
      n("6", "hitApi", "Submit expense report", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{expense_api}/api/v1/reports",
          headers: [
            { key: "Authorization", value: "Bearer {expense_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "employee_id", value: "{employee_id}" },
            { key: "report_id", value: "{report_id}" },
            { key: "total", value: "{total_verified}" },
            { key: "item_count", value: "{pending_count}" },
            { key: "grouped_summary", value: "{grouped_summary}" },
          ],
          variables: [
            {
              name: "data.status",
              customName: "submission_status",
              type: "string",
            },
            {
              name: "data.submitted_at",
              customName: "submitted_at",
              type: "string",
            },
          ],
        },
      }),

      // 7. Tracking confirmation (email)
      emailNode(
        "7",
        "Send tracking confirmation to employee",
        1480,
        "{employee_email}",
        "Expense report {report_id} submitted — ${total_verified} ({pending_count} items)",
        "Hi {employee_name},\\n\\nYour expense report has been submitted successfully.\\n\\nReport ID: {report_id}\\nTotal: ${total_verified}\\nItems: {pending_count}\\n\\nGrouped summary:\\n{grouped_summary}\\n\\nCompliance check: {compliance_issues}\\nMissing receipts: {missing_receipts}\\n\\nYour report is now in the approval queue. You will be notified of any status changes.\\n\\n{policy_answer}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 622. EXPENSE REPORT PROCESSOR (8 nodes)
  // ===================================================================

  "Expense Report Processor": {
    summary:
      "Full-cycle expense report processing from submission through reimbursement with AI extraction, policy validation, GL coding, and approval routing. A report submission trigger runs AI line-item extraction, validates against policy via runCode, applies GL codes, routes for approval, writes back to accounting, processes reimbursement, and logs audit records.",
    nodes: [
      trigger(
        "Webhook: Expense report submitted for processing",
        "webhook_app",
        {
          source: "Expense Management System",
          event: "report.submitted_for_processing",
          howItStarts:
            "An approved expense report is submitted into the processing pipeline for GL coding, accounting writeback, and reimbursement",
          setup:
            "Connect your expense management system to POST approved reports to the workflow URL. Configure accounting system and reimbursement APIs.",
          inputFields: [],
        },
      ),

      // 2. AI line-item extraction
      aiNode(
        "2",
        "AI extract & categorize line items",
        280,
        "You are an expense accounting specialist. Extract every line item from this expense report regardless of format. For each item, identify vendor, amount, category, date, and GL account suggestion. Respond JSON: {employee_name: string, employee_email: string, employee_id: string, report_id: string, line_items: string, line_item_count: number, total_amount: number, currency: string, department: string, cost_center: string}",
        "{webhook_payload}",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_email", as: "employee_email", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
          { field: "report_id", as: "report_id", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          { field: "line_item_count", as: "line_item_count", type: "number" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "currency", as: "currency", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "cost_center", as: "cost_center", type: "string" },
        ],
      ),

      // 3. Policy validation (runCode)
      n("3", "runCode", "Validate against policy & assign GL codes", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate each line item and assign GL codes
const items = JSON.parse(vars.line_items || "[]");
const total = parseFloat(vars.total_amount || "0");
const costCenter = vars.cost_center || "DEFAULT";

const glMapping = {
  meals: "6200", travel: "6300", software: "6400",
  supplies: "6100", entertainment: "6250", transportation: "6310",
  lodging: "6320", telecom: "6410", training: "6500", other: "6900"
};

let codedItems = [];
let validationIssues = [];
let totalCoded = 0;

items.forEach((item, i) => {
  const cat = (item.category || "other").toLowerCase();
  const amount = parseFloat(item.amount || "0");
  const glCode = glMapping[cat] || "6900";
  const fullGL = glCode + "-" + costCenter;
  totalCoded += amount;
  codedItems.push((item.vendor || "Item " + (i + 1)) + ": $" + amount.toFixed(2) + " → " + fullGL);

  if (amount > 1000 && !item.pre_approved) {
    validationIssues.push((item.vendor || "Item " + (i + 1)) + ": $" + amount + " over $1K without pre-approval");
  }
});

const hasIssues = validationIssues.length > 0;

return {
  gl_coded_items: codedItems.join("; "),
  gl_coded_count: codedItems.length,
  total_coded: totalCoded.toFixed(2),
  has_validation_issues: hasIssues ? "yes" : "no",
  validation_issues: validationIssues.join("; ") || "None",
  coding_summary: codedItems.length + " items coded to " + costCenter + " | Total: $" + totalCoded.toFixed(2)
};`,
          outputVariables: [
            { name: "gl_coded_items", type: "string" },
            { name: "gl_coded_count", type: "number" },
            { name: "total_coded", type: "string" },
            { name: "has_validation_issues", type: "string" },
            { name: "validation_issues", type: "string" },
            { name: "coding_summary", type: "string" },
          ],
        },
      }),

      // 4. GL coding — approval routing
      n("4", "condition", "Validation issues found?", 760, {
        conditionData: [
          {
            variable: "has_validation_issues",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Approval routing — issues need review (True path)
      n("5", "approvalGate", "Finance: Review flagged expense items", 1000, {
        approvalGateData: {
          message:
            "Expense Report Processing Review: {report_id}\\n\\nEmployee: {employee_name} ({department})\\nTotal: {currency} {total_amount} | Items: {line_item_count}\\nCost center: {cost_center}\\n\\nCoding: {coding_summary}\\n\\nValidation issues:\\n{validation_issues}\\n\\nApprove to proceed with accounting writeback and reimbursement.",
        },
      }),

      // 6. Accounting writeback
      n("6", "hitApi", "Write to accounting system", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{accounting_api}/api/v1/journal-entries",
          headers: [
            { key: "Authorization", value: "Bearer {accounting_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "report_id", value: "{report_id}" },
            { key: "employee_id", value: "{employee_id}" },
            { key: "total_amount", value: "{total_coded}" },
            { key: "cost_center", value: "{cost_center}" },
            { key: "department", value: "{department}" },
            { key: "line_items", value: "{gl_coded_items}" },
          ],
          variables: [
            {
              name: "data.journal_entry_id",
              customName: "journal_entry_id",
              type: "string",
            },
            {
              name: "data.posted",
              customName: "accounting_posted",
              type: "boolean",
            },
          ],
        },
      }),

      // 7. Reimbursement processing
      n("7", "hitApi", "Process reimbursement", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{payroll_api}/api/v1/reimbursements",
          headers: [
            { key: "Authorization", value: "Bearer {payroll_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "employee_id", value: "{employee_id}" },
            { key: "amount", value: "{total_coded}" },
            { key: "currency", value: "{currency}" },
            { key: "report_id", value: "{report_id}" },
            { key: "journal_entry_id", value: "{journal_entry_id}" },
          ],
          variables: [
            {
              name: "data.reimbursement_id",
              customName: "reimbursement_id",
              type: "string",
            },
            {
              name: "data.payment_date",
              customName: "payment_date",
              type: "string",
            },
          ],
        },
      }),

      // 8. Audit logging (Sheets)
      sheetsNode(
        "8",
        "Log processing audit trail (Sheets)",
        1720,
        "ExpenseProcessingAudit",
        '[["{report_id}","{employee_name}","{department}","{total_coded}","{gl_coded_count}","{cost_center}","{journal_entry_id}","{reimbursement_id}","{payment_date}","{has_validation_issues}","{validation_issues}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // Issues → finance review
      e("4", "6", "output_2"), // No issues → accounting writeback
      e("5", "6"), // After review → accounting writeback
      e("6", "7"), // Accounting → reimbursement
      e("7", "8"), // Reimbursement → audit log
    ],
  },
};
